    // 15oct2626 - Added a 30-second timer for the siren relay when activated by the server consensus command. The relay will automatically turn off after 30 seconds
    // and there is also a new MQTT command "reset_relay" that can be sent to immediately deactivate the relay if needed. This ensures that the siren does not stay on indefinitely in case of any issues with the device or network.
    import React, { useState, useEffect, useRef } from 'react';
    import { useNavigate } from 'react-router-dom';
    // Assuming DeviceMap and LiveGraph are in the same folder
    import DeviceMap from './DeviceMap';
    import LiveGraph from './LiveGraph';
    
    const API_BASE_URL = (
        typeof process !== 'undefined' &&
        process.env &&
        process.env.REACT_APP_API_BASE_URL
    ) || 'http://52.66.78.81:4000';
    const WHATSAPP_ALERT_BASE_URL = 'http://mydoot.com/waews.php?message=';
    const SEISMOGRAPH_TOOL_URL = 'https://shriseismic.com/Graph/';

    const FETCH_TIMEOUT_MS = 15000;

    const fetchWithTimeout = async (url, options = {}, timeoutMs = FETCH_TIMEOUT_MS) => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), timeoutMs);
        try {
            return await fetch(url, { ...options, signal: controller.signal });
        } finally {
            clearTimeout(timeout);
        }
    };

    const formatAckDetails = (ack) => {
        if (!ack || typeof ack !== 'object') {
            return '';
        }

        const parts = [];
        if (ack.command_received) parts.push(`command=${ack.command_received}`);
        if (ack.status) parts.push(`status=${ack.status}`);
        if (ack.ack_for_nonce) parts.push(`nonce=${ack.ack_for_nonce}`);
        if (ack.timestamp) parts.push(`timestamp=${ack.timestamp}`);
        if (ack.received_at) parts.push(`received_at=${ack.received_at}`);
        return parts.join(', ');
    };

    const formatRuntimeFlagsSnapshot = (runtimeFlags) => {
        if (!runtimeFlags || typeof runtimeFlags !== 'object') {
            return '';
        }

        const parts = [];
        if (typeof runtimeFlags.mock_test === 'boolean') parts.push(`mock_test=${runtimeFlags.mock_test}`);
        if (typeof runtimeFlags.live_data_enabled === 'boolean') parts.push(`live_data_enabled=${runtimeFlags.live_data_enabled}`);
        if (typeof runtimeFlags.live_shake_test_log_only === 'boolean') parts.push(`live_shake_test_log_only=${runtimeFlags.live_shake_test_log_only}`);
        if (typeof runtimeFlags.demo_siren_enabled === 'boolean') parts.push(`demo_siren_enabled=${runtimeFlags.demo_siren_enabled}`);
        if (typeof runtimeFlags.iitr_mqtt_enabled === 'boolean') parts.push(`iitr_mqtt_enabled=${runtimeFlags.iitr_mqtt_enabled}`);

        const nvsKeys = runtimeFlags.nvs_keys;
        if (nvsKeys && typeof nvsKeys === 'object') {
            const keyParts = [];
            if (nvsKeys.mock_test) keyParts.push(`mock_test:${nvsKeys.mock_test}`);
            if (nvsKeys.live_data_enabled) keyParts.push(`live_data_enabled:${nvsKeys.live_data_enabled}`);
            if (nvsKeys.live_shake_test_log_only) keyParts.push(`live_shake_test_log_only:${nvsKeys.live_shake_test_log_only}`);
            if (nvsKeys.demo_siren_enabled) keyParts.push(`demo_siren_enabled:${nvsKeys.demo_siren_enabled}`);
            if (nvsKeys.iitr_mqtt_enabled) keyParts.push(`iitr_mqtt_enabled:${nvsKeys.iitr_mqtt_enabled}`);
            if (keyParts.length > 0) {
                parts.push(`nvs_keys=[${keyParts.join(', ')}]`);
            }
        }

        return parts.join(', ');
    };

    const formatCoordinate = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed.toFixed(2) : 'N/A';
    };

    const formatCommandResultMessage = (result, fallbackLabel = 'command') => {
        if (!result || typeof result !== 'object') {
            return `No response received for ${fallbackLabel}.`;
        }

        const lines = [];
        lines.push(result.message || `${fallbackLabel} completed with status ${result.status || 'unknown'}.`);

        if (result.ack) {
            const ackDetails = formatAckDetails(result.ack);
            if (ackDetails) {
                lines.push(`Device ack: ${ackDetails}`);
            }
            if (result.ack.runtime_flags) {
                const runtimeFlags = formatRuntimeFlagsSnapshot(result.ack.runtime_flags);
                if (runtimeFlags) {
                    lines.push(`Saved flags: ${runtimeFlags}`);
                }
            }
        }

        if (result.nonce) {
            lines.push(`Nonce: ${result.nonce}`);
        }

        return lines.join('\n');
    };

    const formatBroadcastResultMessage = (result) => {
        if (!result || typeof result !== 'object') {
            return 'No broadcast response received.';
        }

        const lines = [result.message || 'Broadcast completed.'];
        if (Array.isArray(result.results) && result.results.length > 0) {
            result.results.slice(0, 8).forEach((entry) => {
                const ackLabel = entry?.ack ? formatAckDetails(entry.ack) : '';
                lines.push(`${entry.device_id || 'unknown'}: ${entry.status || 'unknown'}${ackLabel ? ` (${ackLabel})` : ''}`);
            });
            if (result.results.length > 8) {
                lines.push(`...and ${result.results.length - 8} more device(s).`);
            }
        }
        return lines.join('\n');
    };



    // --- Style Definitions ---
    const styles = {
        // --- Styles for Login Form ---
        browserModalContent: {
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '24px', 
            width: '100%', 
            maxWidth: '800px',
            maxHeight: '80vh',
            overflowY: 'auto',
        },
        breadcrumb: {
            marginBottom: '15px',
            padding: '10px',
            backgroundColor: '#f7fafc',
            borderRadius: '6px',
            fontSize: '0.9rem',
        },
        fileList: {
            listStyle: 'none',
            padding: 0,
        },
        fileListItem: {
            padding: '10px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        fileListItemIcon: {
            marginRight: '10px',
            fontSize: '1.2rem',
        },
        fileListItemClickable: {
            flexGrow: 1,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
        },
        loginContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            backgroundColor: '#f7fafc',
        },
        loginBox: {
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            width: '100%',
            maxWidth: '400px',
        },
        loginHeader: {
            fontSize: '1.75rem',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: '24px',
        },
        loginError: {
            color: '#c53030',
            textAlign: 'center',
            marginBottom: '16px',
        },
        loginButton: {
            width: '100%',
            padding: '10px',
            fontSize: '1rem',
            fontWeight: '600',
            color: 'white',
            backgroundColor: '#4299e1',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
        },
        // --- Dashboard Styles ---
        dashboardContainer: {
            fontFamily: 'sans-serif',
            padding: '20px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            backgroundColor: '#fff',
        },
        header: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#2d3748',
            marginBottom: '24px',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            backgroundColor: '#f7fafc',
            padding: '10px 8px',
            textAlign: 'left',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#4a5568',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '1px solid #e2e8f0',
        },
        td: {
            padding: '10px 8px',
            fontSize: '0.8rem',
            color: '#4a5568',
            borderBottom: '1px solid #e2e8f0',
        },
        actionsCell: {
            whiteSpace: 'nowrap',
            textAlign: 'center',
        },
        statusBadge: {
            display: 'inline-flex',
            padding: '4px 8px',
            fontSize: '0.75rem',
            fontWeight: '600',
            lineHeight: '1.25',
            borderRadius: '9999px',
        },
        statusOnline: {
            backgroundColor: '#c6f6d5',
            color: '#2f855a',
        },
        statusOffline: {
            backgroundColor: '#fed7d7',
            color: '#c53030',
        },
        button: {
            padding: '5px 10px',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            margin: '0 4px',
            fontSize: '0.75rem',
        },
        modalOverlay: { 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 100 
        },
        modalContent: { 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '24px', 
            width: '100%', 
            maxWidth: '500px' 
        },
        modalHeader: { 
            fontSize: '1.25rem', 
            fontWeight: 'bold', 
            marginBottom: '24px' 
        },
        inputGroup: {
            marginBottom: '16px'
        },
        inputLabel: {
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: '500'
        },
        textInput: {
            width: '95%', 
            padding: '8px', 
            border: '1px solid #ccc', 
            borderRadius: '4px'
        },
        modalActions: {
            marginTop: '24px', 
            display: 'flex', 
            justifyContent: 'flex-end'
        },
        busyOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        busyOverlayTop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
        },
        busyCard: {
            backgroundColor: '#ffffff',
            padding: '16px 22px',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            minWidth: '240px',
            textAlign: 'center',
            color: '#2d3748',
        },
        busySpinner: {
            width: '34px',
            height: '34px',
            margin: '0 auto 10px auto',
            border: '4px solid #cbd5e0',
            borderTopColor: '#2b6cb0',
            borderRadius: '50%',
            animation: 'adminDashboardSpin 0.8s linear infinite',
        },
    };

    // --- Modal and Browser Components ---

    // --- ADD THE NEW LOG VIEWER MODAL COMPONENT --- 

    const LogViewerModal = ({ device, loggedInUser, authToken, onClose }) => {
        const [logs, setLogs] = useState([]);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState('');

        useEffect(() => {
            const fetchLogs = async () => {
                setIsLoading(true);
                setError('');
                try {
                    // --- THIS URL HAS BEEN CHANGED ---
                    const response = await fetch(`${API_BASE_URL}/api/devices/${device.device_id}/get-log-file`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user: loggedInUser, token: authToken })
                    });
                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || 'Failed to fetch logs.');
                    }
                    const data = await response.json(); // This is now an array of strings
                    setLogs(data.reverse()); // Show newest logs first
                } catch (err) {
                    setError(err.message);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchLogs();
        }, [device.device_id, loggedInUser, authToken]);

        return (
            <div style={styles.modalOverlay}>
                <div style={{...styles.browserModalContent, maxWidth: '900px'}}>
                    <h2 style={styles.modalHeader}>Device Logs for {device.device_id}</h2>
                    <div style={{height: '60vh', overflowY: 'auto', backgroundColor: '#2d3748', color: 'white', fontFamily: 'monospace', padding: '10px', borderRadius: '6px', whiteSpace: 'pre-wrap'}}>
                        {isLoading && <p>Loading logs...</p>}
                        {error && <p style={{color: '#fed7d7'}}>Error: {error}</p>}
                        {!isLoading && logs.length === 0 && <p>No log file found for this device yet.</p>}
                        {!isLoading && logs.map((log, index) => (
                            <div key={index}>{log}</div> // Simply display each line of the log
                        ))}
                    </div>
                    <div style={styles.modalActions}>
                        <button onClick={onClose} style={{...styles.button, backgroundColor: '#a0aec0'}}>Close</button>
                    </div>
                </div>
            </div>
        );
    };

    const SdCardBrowser = ({ device, loggedInUser, authToken, onClose }) => {
        const [contents, setContents] = useState([]);
        const [isLoading, setIsLoading] = useState(true);

        useEffect(() => {
            const fetchData = async () => {
                setIsLoading(true);
                try {
                    // This component fetches the live list from the device via our new server proxy
                    const response = await fetch(`${API_BASE_URL}/api/devices/${device.device_id}/list-sdcard-files`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user: loggedInUser, token: authToken })
                    });
                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || `Failed to fetch file list.`);
                    }
                    const data = await response.json();
                    
                    // Simple logic to create a nested view from a flat list
                    const createTree = (files) => {
                        const tree = [];
                        files.forEach(file => {
                            if(file.path.split('/').length === 2) {
                                tree.push({...file, children: []});
                            }
                        });
                        files.forEach(file => {
                            if(file.path.split('/').length > 2) {
                                const parentName = file.path.split('/')[1];
                                const parent = tree.find(t => t.name === parentName);
                                if(parent) parent.children.push(file);
                            }
                        });
                        return tree;
                    };

                    // For simplicity, this browser will show a two-level view (e.g., year/month)
                    // A more complex recursive function would be needed for infinite depth.
                    setContents(data);

                } catch (error) {
                    console.error("Error browsing SD card data:", error);
                    alert(`Could not load file list from device: ${error.message}`);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchData();
        }, [device.device_id, loggedInUser, authToken]);

        const handleSdCardFolderDownload = async (folderPath) => {
            alert("Starting folder download... The server is now creating a zip file from the device's SD card. This may take some time.");
            try {
                const response = await fetch(`${API_BASE_URL}/api/devices/${device.device_id}/download-sdcard-folder`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user: loggedInUser,
                        token: authToken,
                        path: folderPath
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `Download failed.`);
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                const folderName = folderPath.split('/').pop();
                a.download = `${device.device_id}_${folderName}.zip`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            } catch (error) {
                console.error("Folder download error:", error);
                alert(`Could not download folder: ${error.message}`);
            }
        };
        
        // Group files by top-level folder
        const folderGroups = contents.reduce((acc, file) => {
            const pathParts = file.path.split('/');
            if (pathParts.length > 2) { // e.g., /2025/08
                const groupName = pathParts.slice(0, 2).join('/');
                if (!acc[groupName]) {
                    acc[groupName] = [];
                }
                acc[groupName].push(file);
            }
            return acc;
        }, {});


        return (
            <div style={styles.modalOverlay}>
                <div style={styles.browserModalContent}>
                    <h2 style={styles.modalHeader}>Live SD Card Browser for {device.device_id}</h2>
                    {isLoading ? <p>Loading file list from device...</p> : (
                        <ul style={styles.fileList}>
                            {Object.keys(folderGroups).map(folderPath => (
                                <li key={folderPath} style={styles.fileListItem}>
                                    <div style={styles.fileListItemClickable}>
                                        <span style={styles.fileListItemIcon}>📁</span> {folderPath.substring(1)}
                                    </div>
                                    <button
                                        onClick={() => handleSdCardFolderDownload(folderPath)}
                                        style={{ ...styles.button, backgroundColor: '#319795' }}
                                    >
                                        Download Folder
                                    </button>
                                </li>
                            ))}
                            {contents.length === 0 && !isLoading && <li style={styles.fileListItem}>No files or folders found.</li>}
                        </ul>
                    )}
                    <div style={styles.modalActions}>
                        <button onClick={onClose} style={{...styles.button, backgroundColor: '#a0aec0'}}>Close</button>
                    </div>
                </div>
            </div>
        );
    };

    const DataBrowser = ({ loggedInUser, authToken, onClose }) => {
        const [path, setPath] = useState('');
        const [contents, setContents] = useState([]);
        const [isLoading, setIsLoading] = useState(true);
        const navigate = useNavigate();
        const [downloadingFile, setDownloadingFile] = useState(null); 
        useEffect(() => {
            const fetchData = async () => {
                setIsLoading(true);
                try {
                    const response = await fetch(`${API_BASE_URL}/api/data/browse`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user: loggedInUser, token: authToken, path: path })
                    });
                    
                    if (!response.ok) {
                        throw new Error(`Failed to fetch directory contents. Please Retry. Status: ${response.status}`);
                    }
                    
                    const data = await response.json();
                    setContents(data.contents);
                } catch (error) {
                    console.error("Error browsing data:", error);
                    alert("Could not load directory. It might be empty or an error occurred.");
                } finally {
                    setIsLoading(false);
                }
            };
            fetchData();
        }, [path, loggedInUser, authToken]);

        // --- ADD THIS NEW HANDLER FOR VIEWING THE GRAPH ---
        const handleViewGraph = (item) => {
            // We need to encode the file path to safely pass it in the URL
            const encodedFilePath = encodeURIComponent(path ? `${path}/${item.name}` : item.name);
            window.open(`/seismic-graph-viewer/${encodedFilePath}`, '_blank', 'noopener,noreferrer');
        };
    


        const handleItemClick = async (item) => {
            const newPath = path ? `${path}/${item.name}` : item.name;
            if (item.type === 'dir') {
                setPath(newPath);
            } else {
                setDownloadingFile(item.name); // <--- SET THE FILE BEING DOWNLOADED

                // Logic for single file download
                try {
                    const response = await fetch(`${API_BASE_URL}/api/data/download`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user: loggedInUser, token: authToken, file: newPath })
                    });

                    if (!response.ok) {
                        throw new Error(`Download failed. Status: ${response.status}`);
                    }

                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.href = url;
                    a.download = item.name;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                } catch (error) {
                    console.error("Download error:", error);
                    alert("Could not download the file.");
                } finally {
                    setDownloadingFile(null); // <--- CLEAR THE STATE AFTERWARDS (SUCCESS OR FAIL)
                }
            }
        };

        const handleFolderDownload = async (folderPath) => {
            const deviceId = folderPath.split('/')[0];
            if (!deviceId) {
                alert("Cannot determine the device ID from the path.");
                return;
            }

            alert("Starting folder download... The server is now creating a zip file. This may take some time. Your download will begin shortly.");

            try {
                const response = await fetch(`${API_BASE_URL}/api/data/download-folder`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        user: loggedInUser, 
                        token: authToken,
                        deviceId: deviceId,
                        path: `/${folderPath}`
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `Download failed. Status: ${response.status}`);
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                const folderName = folderPath.split('/').pop();
                a.download = `${folderName}.zip`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();

            } catch (error) {
                console.error("Folder download error:", error);
                alert(`Could not download the folder: ${error.message}`);
            }
        };

        const handleGoUp = () => {
            const pathParts = path.split('/');
            pathParts.pop();
            setPath(pathParts.join('/'));
        };

        const pathBreadcrumbs = path.split('/').filter(p => p);
    

        return (
            <div style={styles.modalOverlay}>
                <div style={styles.browserModalContent}>
                    <h2 style={styles.modalHeader}>Historical Data Browser</h2>
                    <div style={styles.breadcrumb}>
                        <strong>/data/</strong>{pathBreadcrumbs.join('/')}
                    </div>
                    
                    {path && <button onClick={handleGoUp} style={{...styles.button, backgroundColor: '#718096', marginBottom: '15px'}}>&uarr; Go Up</button>}
                    
                    {isLoading ? <p>Loading...</p> : (
                        <ul style={styles.fileList}>
                            {contents.length === 0 && <li style={styles.fileListItem}>No files or folders found.</li>}
                            {contents.map(item => 
                            {
                                // --- ADD THIS HELPER VARIABLE ---
                                const isDownloading = downloadingFile === item.name;

                                return (
                                <li 
                                    key={item.name} 
                                    style={styles.fileListItem} 
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor='#f7fafc'} 
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor='transparent'}
                                >
                                    <div style={styles.fileListItemClickable} onClick={() => handleItemClick(item)}>
                                        <span style={styles.fileListItemIcon}>{item.type === 'dir' ? '📁' : '📄'}</span> 
                                        {item.name}
                                    </div>
                                    {item.type === 'dir' && (
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                            //   const folderPath = path ? `${path}/${item.name}` : item.name;
                                            //  handleFolderDownload(folderPath);
                                            }} 
                                        // style={{...styles.button, backgroundColor: '#319795'}} > Download Folder 
                                        >
                                            
                                        </button>
                                    )}
                                    {item.type === 'file' && item.name.endsWith('.csv') && (
                                        <div style={{whiteSpace: 'nowrap'}}>
                                                                                        <button 
                                                    onClick={(e) => { e.stopPropagation(); handleItemClick(item); }} 
                                                    style={{
                                                        ...styles.button, 
                                                        backgroundColor: isDownloading ? '#a0aec0' : '#3182ce', // Change color when downloading
                                                        cursor: isDownloading ? 'not-allowed' : 'pointer'
                                                    }}
                                                    disabled={isDownloading} // Disable button when downloading
                                                >
                                                    {isDownloading ? 'Downloading...' : 'Download CSV'}
                                                </button>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleViewGraph(item); }} 
                                                style={{...styles.button, backgroundColor: '#38a169', marginLeft: '8px'}}
                                            >
                                                Historical Waveform
                                            </button>
    
                                        </div>
                                    )}
                                    {/* --- END: MODIFIED/NEW BUTTONS BLOCK --- */}

                                </li>
                            )})
                            }
                        </ul>
                    )}
                    <div style={styles.modalActions}>
                        <button onClick={onClose} style={{...styles.button, backgroundColor: '#a0aec0'}}>Close</button>
                    </div>
                </div>
            </div>
        );
    };

    const TopicModal = ({ device, onClose, onSave }) => {
        const [user, setUser] = useState('');
        const [prefix, setPrefix] = useState('');

        const handleSave = () => {
            if (!user || !prefix) {
                alert("Please fill out both fields.");
                return;
            }
            const commandPayload = { topics: { user: user, prefix: prefix } };
            onSave(device.device_id, commandPayload);
            onClose();
        };

        return (
            <div style={styles.modalOverlay}>
                <div style={styles.modalContent}>
                    <h2 style={styles.modalHeader}>Configure Topics for {device.device_id}</h2>
                    <div style={styles.inputGroup}>
                        <label htmlFor="user" style={styles.inputLabel}>MQTT User/Group:</label>
                        <input type="text" id="user" value={user} onChange={(e) => setUser(e.target.value)} placeholder="e.g., vivek" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="prefix" style={styles.inputLabel}>Data Topic Prefix:</label>
                        <input type="text" id="prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="e.g., traces" style={styles.textInput}/>
                    </div>
                    <div style={styles.modalActions}>
                            <button onClick={onClose} style={{...styles.button, backgroundColor: '#a0aec0', marginRight: '8px'}}>Cancel</button>
                        <button onClick={handleSave} style={{...styles.button, backgroundColor: '#48bb78'}}>Save & Restart</button>
                    </div>
                </div>
            </div>
        );
    };

    const OtaModal = ({ device, onClose, onSave }) => {
        const [url, setUrl] = useState('');
        const [expectedVersion, setExpectedVersion] = useState('');

        const handleUpdate = () => {
            let parsedUrl;
            try {
                parsedUrl = new URL(url.trim());
            } catch (err) {
                parsedUrl = null;
            }
            if (!parsedUrl || !['http:', 'https:'].includes(parsedUrl.protocol)) {
                alert("Please enter a valid URL beginning with http or https.");
                return;
            }
            const targetVersion = expectedVersion.trim();
            if (!targetVersion) {
                alert("Please enter the firmware version contained in this .bin file.");
                return;
            }
            if (targetVersion.toLowerCase().endsWith('.bin')) {
                alert("Enter the firmware version only, without the .bin filename extension (for example, SMA_1B).");
                return;
            }
            const commandPayload = { ota: true, url: parsedUrl.href, expected_version: targetVersion };
            onSave(device.device_id, commandPayload);
            onClose();
        };

        return (
            <div style={styles.modalOverlay}>
                <div style={styles.modalContent}>
                    <h2 style={styles.modalHeader}>Firmware Update for {device.device_id}</h2>
                    <div style={styles.inputGroup}>
                        <label htmlFor="ota_url" style={styles.inputLabel}>Firmware URL (.bin file):</label>
                        <input type="text" id="ota_url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="http://example.com/firmware.bin" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="ota_version" style={styles.inputLabel}>Firmware version in this file:</label>
                        <input type="text" id="ota_version" value={expectedVersion} onChange={(e) => setExpectedVersion(e.target.value)} placeholder="e.g. SMA_1B" style={styles.textInput}/>
                    </div>
                    <div style={styles.modalActions}>
                        <button onClick={onClose} style={{...styles.button, backgroundColor: '#a0aec0', marginRight: '8px'}}>Cancel</button>
                        <button onClick={handleUpdate} style={{...styles.button, backgroundColor: '#4299e1'}}>Start Update</button>
                    </div>
                </div>
            </div>
        );
    };

    const WifiModal = ({ device, onClose, onSave }) => {
        const [ssid, setSsid] = useState('');
        const [password, setPassword] = useState('');

        const handleSave = () => {
            if (!ssid) {
                alert("SSID field cannot be empty.");
                return;
            }
            const commandPayload = { wifi: true, username: ssid, password: password };
            onSave(device.device_id, commandPayload);
            onClose();
        };

        return (
            <div style={styles.modalOverlay}>
                <div style={styles.modalContent}>
                    <h2 style={styles.modalHeader}>Add WiFi Network to {device.device_id}</h2>
                    <div style={styles.inputGroup}>
                        <label htmlFor="ssid" style={styles.inputLabel}>Network Name (SSID):</label>
                        <input type="text" id="ssid" value={ssid} onChange={(e) => setSsid(e.target.value)} placeholder="Your WiFi Network Name" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="password" style={styles.inputLabel}>Password:</label>
                        <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="(leave blank for open network)" style={styles.textInput}/>
                    </div>
                    <div style={styles.modalActions}>
                            <button onClick={onClose} style={{...styles.button, backgroundColor: '#a0aec0', marginRight: '8px'}}>Cancel</button>
                        <button onClick={handleSave} style={{...styles.button, backgroundColor: '#48bb78'}}>Save Network</button>
                    </div>
                </div>
            </div>
        );
    };

    const ThresholdModal = ({ device, currentThresholds, onClose, onSave }) => {
        const [tauC, setTauC] = useState(currentThresholds.tau_c || '');
        const [pd, setPd] = useState(currentThresholds.pd || '');
        const [triggerRatio, setTriggerRatio] = useState(currentThresholds.trigger_ratio || '');
        const [minTriggerAccel, setMinTriggerAccel] = useState(currentThresholds.min_trigger_accel || '');
        const [zOffset, setzOffset] = useState(currentThresholds.zOffset || '');
        const [mathuraOnsetSlope, setMathuraOnsetSlope] = useState(currentThresholds.classifier_onset_slope_min ?? currentThresholds.mathura_onset_slope ?? '');
        const [mathuraEntropy, setMathuraEntropy] = useState(currentThresholds.classifier_entropy_min ?? currentThresholds.mathura_entropy ?? '');
        const [mathuraZratio, setMathuraZratio] = useState(currentThresholds.classifier_zratio_min ?? currentThresholds.mathura_zratio ?? '');
        const [mathuraFreqratio, setMathuraFreqratio] = useState(currentThresholds.classifier_freqratio_min ?? currentThresholds.mathura_freqratio ?? '');
        const [mathuraGrowth, setMathuraGrowth] = useState(currentThresholds.classifier_growth_min ?? currentThresholds.mathura_growth ?? '');

        const handleSave = () => {
            const tauC_float = parseFloat(tauC);
            const pd_float = parseFloat(pd);
            const triggerRatioFloat = parseFloat(triggerRatio);
            const minTriggerAccelFloat = parseFloat(minTriggerAccel);
            const z_float = parseFloat(zOffset);
            const onsetSlopeFloat = parseFloat(mathuraOnsetSlope);
            const entropyFloat = parseFloat(mathuraEntropy);
            const zratioFloat = parseFloat(mathuraZratio);
            const freqratioFloat = parseFloat(mathuraFreqratio);
            const growthFloat = parseFloat(mathuraGrowth);

            if ([tauC_float, pd_float, triggerRatioFloat, minTriggerAccelFloat, z_float, onsetSlopeFloat, entropyFloat, zratioFloat, freqratioFloat, growthFloat].some(Number.isNaN)) {
                alert("Please enter valid numbers for thresholds.");
                return;
            }
            
            const commandPayload = { 
                set_thresholds: { 
                    tau_c: tauC_float, 
                    pd: pd_float,
                    trigger_ratio: triggerRatioFloat,
                    min_trigger_accel: minTriggerAccelFloat,
                    zOffset: z_float,
                    classifier_onset_slope_min: onsetSlopeFloat,
                    classifier_entropy_min: entropyFloat,
                    classifier_zratio_min: zratioFloat,
                    classifier_freqratio_min: freqratioFloat,
                    classifier_growth_min: growthFloat
                } 
            };

            onSave(device ? device.device_id : null, commandPayload);
            onClose();
        };

        const isBroadcast = !device;
        const modalTitle = isBroadcast ? "Configure Network-Wide EEW Thresholds" : `Configure Thresholds for ${device.device_id}`;
        const buttonText = isBroadcast ? "Save and Broadcast to All Devices" : "Save to This Device";

        return (
            <div style={styles.modalOverlay}>
                <div style={styles.modalContent}>
                    <h2 style={styles.modalHeader}>{modalTitle}</h2>
                    {isBroadcast && <p style={{marginBottom: '20px', fontSize: '0.8rem', color: '#4a5568'}}>These values will be sent to all online devices. Use with caution.</p>}
                    
                    <div style={styles.inputGroup}>
                        <label htmlFor="tau_c" style={styles.inputLabel}>Characteristic Period (τc) Trigger:</label>
                        <input type="text" id="tau_c" value={tauC} onChange={(e) => setTauC(e.target.value)} placeholder="e.g., 1.0" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="pd" style={styles.inputLabel}>Peak Displacement (Pd) Trigger (cm):</label>
                        <input type="text" id="pd" value={pd} onChange={(e) => setPd(e.target.value)} placeholder="e.g., 0.05" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="trigger_ratio" style={styles.inputLabel}>Trigger Ratio:</label>
                        <input type="text" id="trigger_ratio" value={triggerRatio} onChange={(e) => setTriggerRatio(e.target.value)} placeholder="e.g., 3.2" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="min_trigger_accel" style={styles.inputLabel}>Min Trigger Accel:</label>
                        <input type="text" id="min_trigger_accel" value={minTriggerAccel} onChange={(e) => setMinTriggerAccel(e.target.value)} placeholder="e.g., 0.02" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="zOffset" style={styles.inputLabel}>zOffset (Z) Trigger (cm):</label>
                        <input type="text" id="zOffset" value={zOffset} onChange={(e) => setzOffset(e.target.value)} placeholder="e.g., 2.08 or 0" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="mathura_onset_slope" style={styles.inputLabel}>Classifier Onset Slope Min:</label>
                        <input type="text" id="mathura_onset_slope" value={mathuraOnsetSlope} onChange={(e) => setMathuraOnsetSlope(e.target.value)} placeholder="e.g., 0.04" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="mathura_entropy" style={styles.inputLabel}>Classifier Entropy Min:</label>
                        <input type="text" id="mathura_entropy" value={mathuraEntropy} onChange={(e) => setMathuraEntropy(e.target.value)} placeholder="e.g., 1.8" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="mathura_zratio" style={styles.inputLabel}>Classifier Z-Ratio Min:</label>
                        <input type="text" id="mathura_zratio" value={mathuraZratio} onChange={(e) => setMathuraZratio(e.target.value)} placeholder="e.g., 0.18" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="mathura_freqratio" style={styles.inputLabel}>Classifier Frequency Ratio Min:</label>
                        <input type="text" id="mathura_freqratio" value={mathuraFreqratio} onChange={(e) => setMathuraFreqratio(e.target.value)} placeholder="e.g., 0.85" style={styles.textInput}/>
                    </div>
                    <div style={styles.inputGroup}>
                        <label htmlFor="mathura_growth" style={styles.inputLabel}>Classifier Growth Min:</label>
                        <input type="text" id="mathura_growth" value={mathuraGrowth} onChange={(e) => setMathuraGrowth(e.target.value)} placeholder="e.g., 1.15" style={styles.textInput}/>
                    </div>
                    <div style={styles.modalActions}>
                            <button onClick={onClose} style={{...styles.button, backgroundColor: '#a0aec0', marginRight: '8px'}}>Cancel</button>
                        <button onClick={handleSave} style={{...styles.button, backgroundColor: '#dd6b20'}}>{buttonText}</button>
                    </div>
                </div>
            </div>
        );
    };

    const RuntimeFlagsModal = ({ device, loggedInUser, authToken, onClose, onSaved }) => {
        const [mockTest, setMockTest] = useState(!!device?.soh?.mock_eq_enabled);
        const [liveDataEnabled, setLiveDataEnabled] = useState(device?.soh?.live_data_enabled !== false);
        const [liveShakeTestLogOnly, setLiveShakeTestLogOnly] = useState(device?.soh?.live_shake_test_log_only === true);
        const [demoSirenEnabled, setDemoSirenEnabled] = useState(!!device?.soh?.demo_siren_enabled);
        const [iitrMqttEnabled, setIitrMqttEnabled] = useState(!!device?.soh?.iitr_mqtt_enabled);
        const [isSaving, setIsSaving] = useState(false);
        const [error, setError] = useState('');
        const [status, setStatus] = useState('');

        const handleSave = async () => {
            setIsSaving(true);
            setError('');
            setStatus('');
            try {
                const response = await fetch(`${API_BASE_URL}/api/devices/${device.device_id}/runtime-flags`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user: loggedInUser || 'admin',
                        token: authToken,
                        runtime_flags: {
                            mock_test: mockTest,
                            live_data_enabled: liveDataEnabled,
                            live_shake_test_log_only: liveShakeTestLogOnly,
                            demo_siren_enabled: demoSirenEnabled,
                            iitr_mqtt_enabled: iitrMqttEnabled,
                        },
                    }),
                });
                const data = await response.json();
                if (!data || typeof data !== 'object') {
                    throw new Error(`Server returned an invalid response (${response.status}).`);
                }
                setStatus(formatCommandResultMessage(data, 'runtime flags'));
                onSaved?.(data);
            } catch (e) {
                setError(e.message);
            } finally {
                setIsSaving(false);
            }
        };

        return (
            <div style={styles.modalOverlay}>
                <div style={styles.modalContent}>
                    <h2 style={styles.modalHeader}>Runtime Flags: {device?.device_id}</h2>
                    <p style={{ marginTop: '-8px', marginBottom: '16px', color: '#4a5568', fontSize: '0.9rem' }}>
                        Admin-only live controls for live data, mock replay, and siren suppression.
                    </p>
                    <div style={styles.inputGroup}>
                        <label style={styles.inputLabel}>
                            <input type="checkbox" checked={liveDataEnabled} onChange={(e) => setLiveDataEnabled(e.target.checked)} style={{ marginRight: '8px' }} />
                            Send live data
                        </label>
                    </div>
                    <div style={styles.inputGroup}>
                        <label style={styles.inputLabel}>
                            <input type="checkbox" checked={mockTest} onChange={(e) => setMockTest(e.target.checked)} style={{ marginRight: '8px' }} />
                            Mock earthquake test
                        </label>
                    </div>
                    <div style={styles.inputGroup}>
                        <label style={styles.inputLabel}>
                            <input type="checkbox" checked={liveShakeTestLogOnly} onChange={(e) => setLiveShakeTestLogOnly(e.target.checked)} style={{ marginRight: '8px' }} />
                            LIVE_SHAKE_TEST_LOG_ONLY
                        </label>
                    </div>
                    <div style={styles.inputGroup}>
                        <label style={styles.inputLabel}>
                            <input type="checkbox" checked={demoSirenEnabled} onChange={(e) => setDemoSirenEnabled(e.target.checked)} style={{ marginRight: '8px' }} />
                            Demo siren enabled
                        </label>
                    </div>
                    <div style={styles.inputGroup}>
                        <label style={styles.inputLabel}>
                            <input type="checkbox" checked={iitrMqttEnabled} onChange={(e) => setIitrMqttEnabled(e.target.checked)} style={{ marginRight: '8px' }} />
                            IITR MQTT mirror
                        </label>
                    </div>
                    {error && <p style={styles.loginError}>{error}</p>}
                    {status && <p style={{ color: '#2f855a', whiteSpace: 'pre-wrap' }}>{status}</p>}
                    <div style={styles.modalActions}>
                        <button onClick={onClose} style={{ ...styles.button, backgroundColor: '#a0aec0', marginRight: '8px' }}>Close</button>
                        <button onClick={handleSave} disabled={isSaving} style={{ ...styles.button, backgroundColor: '#2b6cb0' }}>
                            {isSaving ? 'Saving...' : 'Save Flags'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const EewConfigModal = ({ loggedInUser, authToken, onClose }) => {
        const safeParseJsonResponse = async (response) => {
            const rawText = await response.text();
            try {
                return JSON.parse(rawText);
            } catch (e) {
                const preview = rawText ? rawText.slice(0, 120) : 'empty response';
                throw new Error(`Server returned non-JSON response (${response.status}): ${preview}`);
            }
        };

        const [formData, setFormData] = useState({
            minimum_detections: '',
            spatial_window_km: '',
            temporal_window_seconds: '',
            server_min_stalta_ratio: '',
            server_min_pd: '',
            watch_min_pd: '',
            alarm_min_pd: '',
            watch_min_local_confirmed: '',
            alarm_min_local_confirmed: '',
            fw_trigger_tau_c: '',
            fw_trigger_pd: '',
            fw_trigger_ratio: '',
            fw_min_trigger_accel: '',
            fw_z_display_offset: '',
            fw_classifier_onset_slope_min: '',
            fw_classifier_entropy_min: '',
            fw_classifier_zratio_min: '',
            fw_classifier_freqratio_min: '',
            fw_classifier_growth_min: '',
        });
        const [isLoading, setIsLoading] = useState(true);
        const [isSaving, setIsSaving] = useState(false);
        const [error, setError] = useState('');
        const [status, setStatus] = useState('');
        const [selectedProfile, setSelectedProfile] = useState('default');
        const [newProfileName, setNewProfileName] = useState('');
        const [profiles, setProfiles] = useState({});

        useEffect(() => {
            const loadConfig = async () => {
                setIsLoading(true);
                setError('');
                setStatus('');
                try {
                    const response = await fetch(`${API_BASE_URL}/api/eew/config`, {
                        cache: 'no-cache'
                    });
                    const data = await safeParseJsonResponse(response);
                    if (!response.ok) {
                        throw new Error(data.message || 'Failed to load EEW config.');
                    }
                    const loadedProfiles = data.profiles || {};
                    setProfiles(loadedProfiles);
                    const initialProfile = loadedProfiles[selectedProfile] ? selectedProfile : 'default';
                    setSelectedProfile(initialProfile);
                    const config = loadedProfiles[initialProfile] || data;
                    setFormData({
                        minimum_detections: config.minimum_detections ?? '',
                        spatial_window_km: config.spatial_window_km ?? '',
                        temporal_window_seconds: config.temporal_window_seconds ?? '',
                        server_min_stalta_ratio: config.server_min_stalta_ratio ?? '',
                        server_min_pd: config.server_min_pd ?? '',
                        watch_min_pd: config.watch_min_pd ?? '',
                        alarm_min_pd: config.alarm_min_pd ?? '',
                        watch_min_local_confirmed: config.watch_min_local_confirmed ?? '',
                        alarm_min_local_confirmed: config.alarm_min_local_confirmed ?? '',
                        fw_trigger_tau_c: config.fw_trigger_tau_c ?? '',
                        fw_trigger_pd: config.fw_trigger_pd ?? '',
                        fw_trigger_ratio: config.fw_trigger_ratio ?? '',
                        fw_min_trigger_accel: config.fw_min_trigger_accel ?? '',
                        fw_z_display_offset: config.fw_z_display_offset ?? '',
                        fw_classifier_onset_slope_min: config.fw_classifier_onset_slope_min ?? config.fw_mathura_onset_slope ?? '',
                        fw_classifier_entropy_min: config.fw_classifier_entropy_min ?? config.fw_mathura_entropy ?? '',
                        fw_classifier_zratio_min: config.fw_classifier_zratio_min ?? config.fw_mathura_zratio ?? '',
                        fw_classifier_freqratio_min: config.fw_classifier_freqratio_min ?? config.fw_mathura_freqratio ?? '',
                        fw_classifier_growth_min: config.fw_classifier_growth_min ?? config.fw_mathura_growth ?? '',
                    });
                } catch (err) {
                    setError(err.message);
                } finally {
                    setIsLoading(false);
                }
            };

            loadConfig();
        }, []);

        useEffect(() => {
            const loadSelectedProfile = async () => {
                if (!selectedProfile) return;
                setError('');
                setStatus('');
                try {
                    const response = await fetch(`${API_BASE_URL}/api/eew/config`, { cache: 'no-cache' });
                    const data = await safeParseJsonResponse(response);
                    if (!response.ok) {
                        throw new Error(data.message || 'Failed to load EEW config.');
                    }
                    const loadedProfiles = data.profiles || {};
                    setProfiles(loadedProfiles);
                    const config = loadedProfiles[selectedProfile];
                    if (!config) {
                        throw new Error(`Profile '${selectedProfile}' not found.`);
                    }
                    setFormData({
                        minimum_detections: config.minimum_detections ?? '',
                        spatial_window_km: config.spatial_window_km ?? '',
                        temporal_window_seconds: config.temporal_window_seconds ?? '',
                        server_min_stalta_ratio: config.server_min_stalta_ratio ?? '',
                        server_min_pd: config.server_min_pd ?? '',
                        watch_min_pd: config.watch_min_pd ?? '',
                        alarm_min_pd: config.alarm_min_pd ?? '',
                        watch_min_local_confirmed: config.watch_min_local_confirmed ?? '',
                        alarm_min_local_confirmed: config.alarm_min_local_confirmed ?? '',
                        fw_trigger_tau_c: config.fw_trigger_tau_c ?? '',
                        fw_trigger_pd: config.fw_trigger_pd ?? '',
                        fw_trigger_ratio: config.fw_trigger_ratio ?? '',
                        fw_min_trigger_accel: config.fw_min_trigger_accel ?? '',
                        fw_z_display_offset: config.fw_z_display_offset ?? '',
                        fw_classifier_onset_slope_min: config.fw_classifier_onset_slope_min ?? config.fw_mathura_onset_slope ?? '',
                        fw_classifier_entropy_min: config.fw_classifier_entropy_min ?? config.fw_mathura_entropy ?? '',
                        fw_classifier_zratio_min: config.fw_classifier_zratio_min ?? config.fw_mathura_zratio ?? '',
                        fw_classifier_freqratio_min: config.fw_classifier_freqratio_min ?? config.fw_mathura_freqratio ?? '',
                        fw_classifier_growth_min: config.fw_classifier_growth_min ?? config.fw_mathura_growth ?? '',
                    });
                } catch (err) {
                    setError(err.message);
                }
            };
            loadSelectedProfile();
        }, [selectedProfile]);

        const handleChange = (field, value) => {
            setFormData(prev => ({ ...prev, [field]: value }));
        };

        const saveConfig = async () => {
            const payload = {
                user: loggedInUser || 'admin',
                token: authToken,
                profile: selectedProfile,
                minimum_detections: parseInt(formData.minimum_detections, 10),
                spatial_window_km: parseFloat(formData.spatial_window_km),
                temporal_window_seconds: parseFloat(formData.temporal_window_seconds),
                server_min_stalta_ratio: parseFloat(formData.server_min_stalta_ratio),
                server_min_pd: parseFloat(formData.server_min_pd),
                watch_min_pd: parseFloat(formData.watch_min_pd),
                alarm_min_pd: parseFloat(formData.alarm_min_pd),
                watch_min_local_confirmed: parseInt(formData.watch_min_local_confirmed, 10),
                alarm_min_local_confirmed: parseInt(formData.alarm_min_local_confirmed, 10),
                fw_trigger_tau_c: parseFloat(formData.fw_trigger_tau_c),
                fw_trigger_pd: parseFloat(formData.fw_trigger_pd),
                fw_trigger_ratio: parseFloat(formData.fw_trigger_ratio),
                fw_min_trigger_accel: parseFloat(formData.fw_min_trigger_accel),
                fw_z_display_offset: parseFloat(formData.fw_z_display_offset),
                fw_classifier_onset_slope_min: parseFloat(formData.fw_classifier_onset_slope_min),
                fw_classifier_entropy_min: parseFloat(formData.fw_classifier_entropy_min),
                fw_classifier_zratio_min: parseFloat(formData.fw_classifier_zratio_min),
                fw_classifier_freqratio_min: parseFloat(formData.fw_classifier_freqratio_min),
                fw_classifier_growth_min: parseFloat(formData.fw_classifier_growth_min),
            };

            const hasInvalidNumber = Object.entries(payload)
                .filter(([key]) => !['user', 'token', 'profile'].includes(key))
                .some(([, value]) => Number.isNaN(value));
            if (hasInvalidNumber) {
                throw new Error('Please enter valid numeric values for all EEW policy fields.');
            }

            const response = await fetch(`${API_BASE_URL}/api/eew/config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await safeParseJsonResponse(response);
            if (!response.ok) {
                throw new Error(data.message || 'Failed to save EEW config.');
            }

            const savedConfig = data.config || {};
            const nextProfiles = { ...profiles, [selectedProfile]: savedConfig };
            setProfiles(nextProfiles);
            setFormData({
                minimum_detections: savedConfig.minimum_detections ?? '',
                spatial_window_km: savedConfig.spatial_window_km ?? '',
                temporal_window_seconds: savedConfig.temporal_window_seconds ?? '',
                server_min_stalta_ratio: savedConfig.server_min_stalta_ratio ?? '',
                server_min_pd: savedConfig.server_min_pd ?? '',
                watch_min_pd: savedConfig.watch_min_pd ?? '',
                alarm_min_pd: savedConfig.alarm_min_pd ?? '',
                watch_min_local_confirmed: savedConfig.watch_min_local_confirmed ?? '',
                alarm_min_local_confirmed: savedConfig.alarm_min_local_confirmed ?? '',
                fw_trigger_tau_c: savedConfig.fw_trigger_tau_c ?? '',
                fw_trigger_pd: savedConfig.fw_trigger_pd ?? '',
                fw_trigger_ratio: savedConfig.fw_trigger_ratio ?? '',
                fw_min_trigger_accel: savedConfig.fw_min_trigger_accel ?? '',
                fw_z_display_offset: savedConfig.fw_z_display_offset ?? '',
                fw_classifier_onset_slope_min: savedConfig.fw_classifier_onset_slope_min ?? savedConfig.fw_mathura_onset_slope ?? '',
                fw_classifier_entropy_min: savedConfig.fw_classifier_entropy_min ?? savedConfig.fw_mathura_entropy ?? '',
                fw_classifier_zratio_min: savedConfig.fw_classifier_zratio_min ?? savedConfig.fw_mathura_zratio ?? '',
                fw_classifier_freqratio_min: savedConfig.fw_classifier_freqratio_min ?? savedConfig.fw_mathura_freqratio ?? '',
                fw_classifier_growth_min: savedConfig.fw_classifier_growth_min ?? savedConfig.fw_mathura_growth ?? '',
            });
            return savedConfig;
        };

        const handleSave = async () => {
            setIsSaving(true);
            setError('');
            setStatus('');
            try {
                await saveConfig();
                setStatus('EEW server policy updated successfully.');
            } catch (err) {
                setError(err.message);
            } finally {
                setIsSaving(false);
            }
        };

        const handleSaveAndApplyFirmware = async () => {
            setIsSaving(true);
            setError('');
            setStatus('');
            try {
                await saveConfig();
                const response = await fetch(`${API_BASE_URL}/api/eew/config/apply-firmware`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user: loggedInUser || 'admin',
                        token: authToken,
                        profile: selectedProfile,
                    }),
                });
                const data = await safeParseJsonResponse(response);
                if (!response.ok) {
                    throw new Error(data.message || 'Failed to apply firmware thresholds.');
                }
                setStatus(`EEW policy saved and firmware thresholds pushed to ${data.device_count || 0} device(s).`);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsSaving(false);
            }
        };

        const handleCreateProfile = async () => {
            const profile = (newProfileName || '').trim();
            if (!profile) {
                setError('Enter a profile name.');
                return;
            }
            setIsSaving(true);
            setError('');
            setStatus('');
            try {
                const response = await fetch(`${API_BASE_URL}/api/eew/config`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user: loggedInUser || 'admin', token: authToken, profile }),
                });
                const data = await safeParseJsonResponse(response);
                if (!response.ok) {
                    throw new Error(data.message || 'Failed to create profile.');
                }
                setProfiles(prev => ({ ...prev, [profile]: data.config || {} }));
                setSelectedProfile(profile);
                setStatus(`Profile '${profile}' is ready.`);
                setNewProfileName('');
            } catch (err) {
                setError(err.message);
            } finally {
                setIsSaving(false);
            }
        };

        const handleDeleteProfile = async () => {
            const profile = selectedProfile;
            if (!profile) {
                setError('Select a profile to delete.');
                return;
            }
            if (profile === 'default') {
                setError('The default profile is protected and cannot be deleted.');
                return;
            }
            if (!window.confirm(`Delete EEW profile '${profile}'? Profiles assigned to devices cannot be deleted.`)) {
                return;
            }

            setIsSaving(true);
            setError('');
            setStatus('');
            try {
                const response = await fetch(`${API_BASE_URL}/api/eew/config/${encodeURIComponent(profile)}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user: loggedInUser || 'admin', token: authToken }),
                });
                const data = await safeParseJsonResponse(response);
                if (!response.ok) {
                    const attached = Array.isArray(data.attached_devices) && data.attached_devices.length
                        ? ` Attached devices: ${data.attached_devices.join(', ')}`
                        : '';
                    throw new Error(`${data.message || 'Failed to delete profile.'}${attached}`);
                }
                const nextProfiles = { ...profiles };
                delete nextProfiles[profile];
                setProfiles(nextProfiles);
                setSelectedProfile(nextProfiles.default ? 'default' : Object.keys(nextProfiles)[0] || '');
                setStatus(`Profile '${profile}' deleted.`);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsSaving(false);
            }
        };

        return (
            <div style={styles.modalOverlay}>
                <div style={{ ...styles.browserModalContent, maxWidth: '760px' }}>
                    <h2 style={styles.modalHeader}>Configure Server EEW Policy</h2>
                    <p style={{ marginTop: '-8px', marginBottom: '20px', color: '#4a5568', fontSize: '0.9rem' }}>
                        This changes the confirmation policy on the server side for watch and alarm decisions, and stores a firmware threshold bundle for devices assigned to this profile.
                    </p>
                    <p style={{ marginTop: '-12px', marginBottom: '16px', color: '#718096', fontSize: '0.85rem' }}>
                        Active profile version: {profiles[selectedProfile]?.profile_version ?? 1}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '12px', marginBottom: '16px' }}>
                        <div style={styles.inputGroup}>
                            <label style={styles.inputLabel}>Profile</label>
                            <select value={selectedProfile} onChange={(e) => setSelectedProfile(e.target.value)} style={styles.textInput}>
                                {Object.keys(profiles).map((p) => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.inputLabel}>New Profile</label>
                            <input type="text" value={newProfileName} onChange={(e) => setNewProfileName(e.target.value)} placeholder="e.g., ncr_core" style={styles.textInput} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button onClick={handleCreateProfile} disabled={isSaving} style={{ ...styles.button, backgroundColor: '#4a5568' }}>Create</button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button
                                onClick={handleDeleteProfile}
                                disabled={isSaving || isLoading || selectedProfile === 'default'}
                                style={{
                                    ...styles.button,
                                    backgroundColor: selectedProfile === 'default' ? '#e2e8f0' : '#c53030',
                                    color: selectedProfile === 'default' ? '#718096' : 'white',
                                    cursor: selectedProfile === 'default' ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>

                    {isLoading ? (
                        <p>Loading current EEW policy...</p>
                    ) : (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))', gap: '16px' }}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Minimum Detections</label>
                                    <input type="number" min="2" step="1" value={formData.minimum_detections} onChange={(e) => handleChange('minimum_detections', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Spatial Window (km)</label>
                                    <input type="number" min="0.1" step="0.1" value={formData.spatial_window_km} onChange={(e) => handleChange('spatial_window_km', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Temporal Window (s)</label>
                                    <input type="number" min="0.1" step="0.1" value={formData.temporal_window_seconds} onChange={(e) => handleChange('temporal_window_seconds', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Server Min STA/LTA</label>
                                    <input type="number" min="0.1" step="0.1" value={formData.server_min_stalta_ratio} onChange={(e) => handleChange('server_min_stalta_ratio', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Server Min Pd</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.server_min_pd} onChange={(e) => handleChange('server_min_pd', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Watch Min Pd</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.watch_min_pd} onChange={(e) => handleChange('watch_min_pd', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Alarm Min Pd</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.alarm_min_pd} onChange={(e) => handleChange('alarm_min_pd', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Watch Min Local Confirmed</label>
                                    <input type="number" min="1" step="1" value={formData.watch_min_local_confirmed} onChange={(e) => handleChange('watch_min_local_confirmed', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Alarm Min Local Confirmed</label>
                                    <input type="number" min="1" step="1" value={formData.alarm_min_local_confirmed} onChange={(e) => handleChange('alarm_min_local_confirmed', e.target.value)} style={styles.textInput} />
                                </div>
                            </div>

                            <h3 style={{ marginTop: '22px', marginBottom: '12px', color: '#2d3748' }}>Firmware Threshold Bundle</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))', gap: '16px' }}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Firmware Trigger TauC</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.fw_trigger_tau_c} onChange={(e) => handleChange('fw_trigger_tau_c', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Firmware Trigger Pd</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.fw_trigger_pd} onChange={(e) => handleChange('fw_trigger_pd', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Firmware Trigger Ratio</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.fw_trigger_ratio} onChange={(e) => handleChange('fw_trigger_ratio', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Firmware Min Trigger Accel</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.fw_min_trigger_accel} onChange={(e) => handleChange('fw_min_trigger_accel', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Firmware Z Display Offset</label>
                                    <input type="number" step="0.0001" value={formData.fw_z_display_offset} onChange={(e) => handleChange('fw_z_display_offset', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Classifier Onset Slope Min</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.fw_classifier_onset_slope_min} onChange={(e) => handleChange('fw_classifier_onset_slope_min', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Classifier Entropy Min</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.fw_classifier_entropy_min} onChange={(e) => handleChange('fw_classifier_entropy_min', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Classifier Z-Ratio Min</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.fw_classifier_zratio_min} onChange={(e) => handleChange('fw_classifier_zratio_min', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Classifier Frequency Ratio Min</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.fw_classifier_freqratio_min} onChange={(e) => handleChange('fw_classifier_freqratio_min', e.target.value)} style={styles.textInput} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Classifier Growth Min</label>
                                    <input type="number" min="0.0001" step="0.0001" value={formData.fw_classifier_growth_min} onChange={(e) => handleChange('fw_classifier_growth_min', e.target.value)} style={styles.textInput} />
                                </div>
                            </div>

                            {error && <p style={{ ...styles.loginError, textAlign: 'left', marginTop: '8px' }}>{error}</p>}
                            {status && <p style={{ color: '#2f855a', marginTop: '8px' }}>{status}</p>}
                        </>
                    )}

                    <div style={styles.modalActions}>
                        <button onClick={onClose} style={{ ...styles.button, backgroundColor: '#a0aec0', marginRight: '8px' }}>Close</button>
                        <button onClick={handleSaveAndApplyFirmware} disabled={isLoading || isSaving} style={{ ...styles.button, backgroundColor: '#dd6b20', marginRight: '8px', opacity: (isLoading || isSaving) ? 0.7 : 1 }}>
                            {isSaving ? 'Saving...' : 'Save And Push To Profile Devices'}
                        </button>
                        <button onClick={handleSave} disabled={isLoading || isSaving} style={{ ...styles.button, backgroundColor: '#2b6cb0', opacity: (isLoading || isSaving) ? 0.7 : 1 }}>
                            {isSaving ? 'Saving...' : 'Save Server Policy'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const DeviceAssignmentModal = ({ device, loggedInUser, authToken, onClose, onSaved }) => {
        const [clusterId, setClusterId] = useState(device?.cluster_id || 'default_cluster');
        const [assignedProfile, setAssignedProfile] = useState(device?.assigned_profile || 'default');
        const [profiles, setProfiles] = useState([]);
        const [error, setError] = useState('');
        const [status, setStatus] = useState('');
        const [isSaving, setIsSaving] = useState(false);

        useEffect(() => {
            const loadProfiles = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}/api/eew/config`, { cache: 'no-cache' });
                    const data = await response.json();
                    if (!response.ok) throw new Error(data.message || 'Failed to load profiles');
                    const profileNames = Object.keys(data.profiles || {});
                    setProfiles(profileNames);
                    if (profileNames.length > 0 && !profileNames.includes(assignedProfile)) {
                        setAssignedProfile(profileNames[0]);
                    }
                } catch (e) {
                    setError(e.message);
                }
            };
            loadProfiles();
        }, []);

        const handleSave = async () => {
            setIsSaving(true);
            setError('');
            setStatus('');
            try {
                const response = await fetch(`${API_BASE_URL}/api/devices/${device.device_id}/assignment`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user: loggedInUser || 'admin',
                        token: authToken,
                        cluster_id: clusterId,
                        assigned_profile: assignedProfile,
                    }),
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || 'Failed to save assignment');
                const autoPush = data.auto_push;
                const pushSuffix = autoPush?.attempted
                    ? (autoPush.status === 'success' ? ' Profile pushed to device.' : ` Profile push failed: ${autoPush.message || autoPush.status}`)
                    : '';
                setStatus(`Assignment updated successfully.${pushSuffix}`);
                onSaved?.(data);
            } catch (e) {
                setError(e.message);
            } finally {
                setIsSaving(false);
            }
        };

        return (
            <div style={styles.modalOverlay}>
                <div style={styles.modalContent}>
                    <h2 style={styles.modalHeader}>Device Assignment: {device?.device_id}</h2>
                    <div style={styles.inputGroup}>
                        <label style={styles.inputLabel}>Cluster ID</label>
                        <input type="text" value={clusterId} onChange={(e) => setClusterId(e.target.value)} style={styles.textInput} />
                    </div>
                    <div style={styles.inputGroup}>
                        <label style={styles.inputLabel}>Assigned Profile</label>
                        <select value={assignedProfile} onChange={(e) => setAssignedProfile(e.target.value)} style={styles.textInput}>
                            {profiles.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                    {error && <p style={styles.loginError}>{error}</p>}
                    {status && <p style={{ color: '#2f855a' }}>{status}</p>}
                    <div style={styles.modalActions}>
                        <button onClick={onClose} style={{...styles.button, backgroundColor: '#a0aec0', marginRight: '8px'}}>Close</button>
                        <button onClick={handleSave} disabled={isSaving} style={{...styles.button, backgroundColor: '#2b6cb0'}}>
                            {isSaving ? 'Saving...' : 'Save Assignment'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const DeviceRegistryModal = ({ loggedInUser, authToken, onClose, onSaved }) => {
        const [profiles, setProfiles] = useState([]);
        const [devices, setDevices] = useState([]);
        const [selectedId, setSelectedId] = useState('__new__');
        const [form, setForm] = useState({
            device_id: '',
            display_name: '',
            lat: '',
            lon: '',
            cluster_id: 'default_cluster',
            assigned_profile: 'default',
        });
        const [isLoading, setIsLoading] = useState(true);
        const [isSaving, setIsSaving] = useState(false);
        const [error, setError] = useState('');
        const [status, setStatus] = useState('');

        const loadData = async () => {
            setIsLoading(true);
            setError('');
            try {
                const [profileRes, deviceRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/eew/config`, { cache: 'no-cache' }),
                    fetch(`${API_BASE_URL}/api/devices/registry`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user: loggedInUser, token: authToken }),
                    }),
                ]);
                const profileData = await profileRes.json();
                const deviceData = await deviceRes.json();
                if (!profileRes.ok) throw new Error(profileData.message || 'Failed to load profiles');
                if (!deviceRes.ok) throw new Error(deviceData.message || 'Failed to load devices');
                const p = Object.keys(profileData.profiles || {});
                setProfiles(p);
                setDevices(deviceData.devices || []);
            } catch (e) {
                setError(e.message);
            } finally {
                setIsLoading(false);
            }
        };

        useEffect(() => {
            loadData();
        }, []);

        useEffect(() => {
            if (selectedId === '__new__') {
                setForm(prev => ({ ...prev, device_id: '', display_name: '', lat: '', lon: '' }));
                return;
            }
            const found = devices.find(d => d.device_id === selectedId);
            if (!found) return;
            setForm({
                device_id: found.device_id || '',
                display_name: found.display_name || found.device_id || '',
                lat: String(found.lat ?? ''),
                lon: String(found.lon ?? ''),
                cluster_id: found.cluster_id || 'default_cluster',
                assigned_profile: found.assigned_profile || (profiles[0] || 'default'),
            });
        }, [selectedId, devices, profiles]);

        const handleSave = async () => {
            setIsSaving(true);
            setError('');
            setStatus('');
            try {
                const payload = {
                    user: loggedInUser,
                    token: authToken,
                    device_id: form.device_id.trim(),
                    display_name: form.display_name.trim(),
                    lat: Number(form.lat),
                    lon: Number(form.lon),
                    cluster_id: form.cluster_id.trim(),
                    assigned_profile: form.assigned_profile,
                };
                const response = await fetch(`${API_BASE_URL}/api/devices/upsert`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || 'Save failed');
                const autoPush = data.auto_push;
                const pushSuffix = autoPush?.attempted
                    ? (autoPush.status === 'success' ? ' and pushed profile to device' : ` but profile push failed: ${autoPush.message || autoPush.status}`)
                    : '';
                setStatus(`Saved ${payload.device_id}${pushSuffix}`);
                await loadData();
                onSaved?.();
            } catch (e) {
                setError(e.message);
            } finally {
                setIsSaving(false);
            }
        };

        const handleDelete = async () => {
            if (selectedId === '__new__') return;
            if (!window.confirm(`Delete device ${selectedId} from registry?`)) return;
            setError('');
            setStatus('');
            try {
                const response = await fetch(`${API_BASE_URL}/api/devices/${selectedId}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user: loggedInUser, token: authToken }),
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || 'Delete failed');
                setStatus(`Deleted ${selectedId}`);
                setSelectedId('__new__');
                await loadData();
                onSaved?.();
            } catch (e) {
                setError(e.message);
            }
        };

        return (
            <div style={styles.modalOverlay}>
                <div style={{ ...styles.browserModalContent, maxWidth: '920px' }}>
                    <h2 style={styles.modalHeader}>Device Registry</h2>
                    {isLoading ? <p>Loading registry...</p> : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div>
                                <label style={styles.inputLabel}>Select Existing Device</label>
                                <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={styles.textInput}>
                                    <option value="__new__">+ Add New Device</option>
                                    {devices.map(d => (
                                        <option key={d.device_id} value={d.device_id}>
                                            {d.device_id} ({d.display_name || d.device_id})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div />
                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>Device ID</label>
                                <input value={form.device_id} disabled={selectedId !== '__new__'} onChange={(e) => setForm(prev => ({ ...prev, device_id: e.target.value }))} style={styles.textInput} />
                            </div>
                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>Display Name</label>
                                <input value={form.display_name} onChange={(e) => setForm(prev => ({ ...prev, display_name: e.target.value }))} style={styles.textInput} />
                            </div>
                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>Latitude</label>
                                <input type="number" step="0.01" value={form.lat} onChange={(e) => setForm(prev => ({ ...prev, lat: e.target.value }))} style={styles.textInput} />
                            </div>
                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>Longitude</label>
                                <input type="number" step="0.01" value={form.lon} onChange={(e) => setForm(prev => ({ ...prev, lon: e.target.value }))} style={styles.textInput} />
                            </div>
                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>Cluster ID</label>
                                <input value={form.cluster_id} onChange={(e) => setForm(prev => ({ ...prev, cluster_id: e.target.value }))} style={styles.textInput} />
                            </div>
                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>Assigned Profile</label>
                                <select value={form.assigned_profile} onChange={(e) => setForm(prev => ({ ...prev, assigned_profile: e.target.value }))} style={styles.textInput}>
                                    {(profiles.length ? profiles : ['default']).map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                        </div>
                    )}
                    {error && <p style={styles.loginError}>{error}</p>}
                    {status && <p style={{ color: '#2f855a' }}>{status}</p>}
                    <div style={styles.modalActions}>
                        <button onClick={onClose} style={{ ...styles.button, backgroundColor: '#a0aec0', marginRight: '8px' }}>Close</button>
                        {selectedId !== '__new__' && (
                            <button onClick={handleDelete} style={{ ...styles.button, backgroundColor: '#c53030', marginRight: '8px' }}>Delete</button>
                        )}
                        <button onClick={handleSave} disabled={isSaving || isLoading} style={{ ...styles.button, backgroundColor: '#2b6cb0' }}>
                            {isSaving ? 'Saving...' : 'Save Device'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // --- Main Dashboard Component ---

    const AdminDashboard = () => {
        // --- State Variables ---
        const [eewEvents, setEewEvents] = useState([]);
        const [countdown, setCountdown] = useState(null);
        const [isAuthenticated, setIsAuthenticated] = useState(false);
        const [loggedInUser, setLoggedInUser] = useState(null);
        const [authToken, setAuthToken] = useState(null);
        const [username, setUsername] = useState('');
        const [password, setPassword] = useState('');
        const [loginError, setLoginError] = useState('');
        const [devices, setDevices] = useState([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [selectedDevice, setSelectedDevice] = useState(null);
        const [isTopicModalOpen, setTopicModalOpen] = useState(false);
        const [isOtaModalOpen, setOtaModalOpen] = useState(false);
        const [isWifiModalOpen, setWifiModalOpen] = useState(false);
        const [isThresholdModalOpen, setThresholdModalOpen] = useState(false);
        const [isRuntimeFlagsModalOpen, setRuntimeFlagsModalOpen] = useState(false);
        const [isEewConfigModalOpen, setEewConfigModalOpen] = useState(false);
        const [isAssignmentModalOpen, setAssignmentModalOpen] = useState(false);
        const [isRegistryModalOpen, setRegistryModalOpen] = useState(false);
        const [currentThresholds, setCurrentThresholds] = useState({
            tau_c: 0.8,
            pd: 0.02,
            trigger_ratio: 3.2,
            min_trigger_accel: 0.02,
            zOffset: 2.08,
            classifier_profile_name: 'default',
            classifier_profile_version: 1,
            classifier_onset_slope_min: 0.04,
            classifier_entropy_min: 1.8,
            classifier_zratio_min: 0.18,
            classifier_freqratio_min: 0.85,
            classifier_growth_min: 1.15,
            mathura_onset_slope: 0.04,
            mathura_entropy: 1.8,
            mathura_zratio: 0.18,
            mathura_freqratio: 0.85,
            mathura_growth: 1.15,
        });
        const [confirmedEvents, setConfirmedEvents] = useState([]);
        const [candidateGateDebug, setCandidateGateDebug] = useState([]);
        const prevDevicesRef = useRef();
        const [showMap, setShowMap] = useState(false);
        const [showGraph, setShowGraph] = useState(false);
        const [deviceDriftFilter, setDeviceDriftFilter] = useState('all');
        const [isDataBrowserOpen, setIsDataBrowserOpen] = useState(false);
        const [isSdCardBrowserOpen, setIsSdCardBrowserOpen] = useState(false);
        const [isLogViewerOpen, setIsLogViewerOpen] = useState(false);
    const handleGraph = () => {
            window.open(SEISMOGRAPH_TOOL_URL, '_blank', 'noopener,noreferrer');
        };

        const buildThresholdStateFromProfile = (profileName, profile) => ({
            tau_c: profile?.fw_trigger_tau_c ?? 0.8,
            pd: profile?.fw_trigger_pd ?? 0.02,
            trigger_ratio: profile?.fw_trigger_ratio ?? 3.2,
            min_trigger_accel: profile?.fw_min_trigger_accel ?? 0.02,
            zOffset: profile?.fw_z_display_offset ?? profile?.zDisplayOffset ?? profile?.fw_zDisplayOffset ?? 2.08,
            classifier_profile_name: profileName || 'default',
            classifier_profile_version: profile?.profile_version ?? 1,
            classifier_onset_slope_min: profile?.fw_classifier_onset_slope_min ?? profile?.fw_mathura_onset_slope ?? 0.04,
            classifier_entropy_min: profile?.fw_classifier_entropy_min ?? profile?.fw_mathura_entropy ?? 1.8,
            classifier_zratio_min: profile?.fw_classifier_zratio_min ?? profile?.fw_mathura_zratio ?? 0.18,
            classifier_freqratio_min: profile?.fw_classifier_freqratio_min ?? profile?.fw_mathura_freqratio ?? 0.85,
            classifier_growth_min: profile?.fw_classifier_growth_min ?? profile?.fw_mathura_growth ?? 1.15,
            mathura_onset_slope: profile?.fw_mathura_onset_slope ?? profile?.fw_classifier_onset_slope_min ?? 0.04,
            mathura_entropy: profile?.fw_mathura_entropy ?? profile?.fw_classifier_entropy_min ?? 1.8,
            mathura_zratio: profile?.fw_mathura_zratio ?? profile?.fw_classifier_zratio_min ?? 0.18,
            mathura_freqratio: profile?.fw_mathura_freqratio ?? profile?.fw_classifier_freqratio_min ?? 0.85,
            mathura_growth: profile?.fw_mathura_growth ?? profile?.fw_classifier_growth_min ?? 1.15,
        });

        const loadThresholdPreset = async (profileName = 'default') => {
            const response = await fetchWithTimeout(`${API_BASE_URL}/api/eew/config`, { cache: 'no-cache' }, 15000);
            if (!response.ok) {
                throw new Error(`Failed to load EEW profile presets. Status: ${response.status}`);
            }
            const data = await response.json();
            const profiles = data?.profiles || {};
            const resolvedProfileName = profiles[profileName] ? profileName : 'default';
            return buildThresholdStateFromProfile(resolvedProfileName, profiles[resolvedProfileName] || {});
        };

        const getProfileDriftState = (device) => {
            const assignedName = device?.assigned_profile || 'default';
            const assignedVersionRaw = Number(device?.assigned_profile_version ?? 1);
            const assignedVersion = Number.isFinite(assignedVersionRaw) ? assignedVersionRaw : 1;
            const appliedName = device?.soh?.classifier_profile_name || '';
            const rawAppliedVersion = device?.soh?.classifier_profile_version;
            const parsedAppliedVersion = rawAppliedVersion === undefined || rawAppliedVersion === null || rawAppliedVersion === ''
                ? null
                : Number(rawAppliedVersion);
            const appliedVersion = parsedAppliedVersion !== null && Number.isFinite(parsedAppliedVersion)
                ? parsedAppliedVersion
                : null;

            if (!appliedName) {
                return {
                    label: 'Not Reported',
                    detail: 'Device has not reported applied classifier tuning yet.',
                    tone: 'unknown'
                };
            }

            const nameMismatch = appliedName !== assignedName;
            const versionMismatch = appliedVersion !== null && appliedVersion !== assignedVersion;

            if (nameMismatch || versionMismatch) {
                return {
                    label: 'Drift',
                    detail: `${appliedName} v${appliedVersion ?? '?'}`,
                    tone: 'drift'
                };
            }

            return {
                label: 'In Sync',
                detail: `${appliedName} v${appliedVersion ?? assignedVersion}`,
                tone: 'ok'
            };
        };

        const filteredDevices = devices.filter((device) => {
            const driftState = getProfileDriftState(device);
            if (deviceDriftFilter === 'drift') {
                return driftState.tone === 'drift';
            }
            if (deviceDriftFilter === 'unknown') {
                return driftState.tone === 'unknown';
            }
            if (deviceDriftFilter === 'ok') {
                return driftState.tone === 'ok';
            }
            return true;
        });

        const driftSummary = devices.reduce((acc, device) => {
            const tone = getProfileDriftState(device).tone;
            if (tone === 'drift') acc.drift += 1;
            else if (tone === 'ok') acc.ok += 1;
            else acc.unknown += 1;
            return acc;
        }, { drift: 0, ok: 0, unknown: 0 });

        const getDisplayConfidence = (device) => {
            const rawConfidence = device?.last_confidence ?? device?.soh?.last_confidence ?? device?.soh?.confidence;
            const parsedConfidence = rawConfidence === undefined || rawConfidence === null || rawConfidence === ''
                ? null
                : Number(rawConfidence);
            return Number.isFinite(parsedConfidence) ? parsedConfidence : null;
        };



        // --- Command Handlers ---

    const openLogViewer = (device) => {
            setSelectedDevice(device);
            setIsLogViewerOpen(true);
        };

        const openSdCardBrowser = (device) => {
            setSelectedDevice(device);
            setIsSdCardBrowserOpen(true);
        };

        const handleCommand = async (deviceId, commandPayload) => {
            const isObject = typeof commandPayload === 'object' && commandPayload !== null;
            const body = isObject ? commandPayload : { command: commandPayload };
            const payloadWithUser = { ...body, user: loggedInUser, token: authToken };
            try {
                const response = await fetchWithTimeout(`${API_BASE_URL}/api/devices/${deviceId}/command`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payloadWithUser),
                });
                const result = await response.json();
                if (!result || typeof result !== 'object') {
                    throw new Error(`Server returned an invalid response (${response.status}).`);
                }
                if (body.ota === true) {
                    const nonce = result.nonce;
                    if (!nonce) {
                        alert(formatCommandResultMessage(result, 'OTA'));
                        return;
                    }
                    if (result.ack?.status === 'REJECTED') {
                        alert(`OTA request rejected by device: ${result.ack.detail || 'request_rejected'}`);
                        return;
                    }

                    const deadline = Date.now() + 5 * 60 * 1000;
                    let otaResult = null;
                    while (Date.now() < deadline) {
                        await new Promise(resolve => setTimeout(resolve, 3000));
                        const statusResponse = await fetchWithTimeout(`${API_BASE_URL}/api/devices/${deviceId}/ota-status`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ user: loggedInUser, token: authToken, nonce }),
                        });
                        const statusData = await statusResponse.json();
                        if (!statusResponse.ok) {
                            throw new Error(statusData.message || `OTA status check failed (${statusResponse.status}).`);
                        }
                        if (statusData.ota) {
                            otaResult = statusData.ota;
                            if (otaResult.status === 'COMPLETE' || otaResult.status === 'FAILED' || otaResult.status === 'REJECTED' || otaResult.status === 'UNVERIFIED') {
                                break;
                            }
                        }
                    }

                    if (otaResult?.status === 'COMPLETE') {
                        alert(`OTA complete. Device rebooted and confirmed firmware ${otaResult.firmware_version || body.expected_version}.`);
                    } else if (otaResult?.status === 'UNVERIFIED') {
                        const reportedVersion = String(otaResult.firmware_version || '').trim();
                        const requestedVersion = String(body.expected_version || '').trim();
                        if (reportedVersion && requestedVersion && reportedVersion === requestedVersion) {
                            alert(`OTA appears successful: the device rebooted and reports ${reportedVersion}, matching the requested version. The firmware could not verify it from saved OTA metadata.`);
                        } else {
                            alert(`Device rebooted with firmware ${reportedVersion || 'version unknown'}, but could not verify it against the requested version ${requestedVersion || '(not provided)'}.`);
                        }
                    } else if (otaResult?.status === 'FAILED' || otaResult?.status === 'REJECTED') {
                        alert(`OTA ${otaResult.status.toLowerCase()}: ${otaResult.detail || 'no additional details'}${otaResult.firmware_version ? ` (device firmware: ${otaResult.firmware_version})` : ''}`);
                    } else {
                        alert(`OTA is still pending. The device has not reported a final result yet. Command reference: ${nonce}`);
                    }
                    return;
                }
                const label = body.command || body.runtime_flags || 'command';
                alert(formatCommandResultMessage(result, label));
            } catch (err) {
                const msg = err?.name === 'AbortError' ? 'Request timed out. Server/network is slow.' : err.message;
                alert(`Error: ${msg}`);
            }
        };
        
        const handleBroadcastCommand = async (commandPayload) => {
            const payloadWithUser = { ...commandPayload, user: loggedInUser, token: authToken };
            try {
                const response = await fetchWithTimeout(`${API_BASE_URL}/api/command/broadcast`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payloadWithUser),
                });
                const result = await response.json();
                if (!result || typeof result !== 'object') {
                    throw new Error(`Server returned an invalid response (${response.status}).`);
                }
                alert(formatBroadcastResultMessage(result));
            } catch (err) {
                const msg = err?.name === 'AbortError' ? 'Request timed out. Server/network is slow.' : err.message;
                alert(`Error: ${msg}`);
            }
        };

        const handleThresholdSave = (deviceId, payload) => {
            if (deviceId) {
                handleCommand(deviceId, payload);
            } else {
                handleBroadcastCommand(payload);
            }
        };
        
        const notifyDeviceOffline = async (device) => {
            const url = `${WHATSAPP_ALERT_BASE_URL} *OFFLINE:* `;
            const finalurl = url + device.device_id + " Lat: " + device.lat + " Long: " + device.lon;
            try {
                fetch(finalurl, { mode: 'no-cors' });
            } catch (err) {
                console.error("Failed to send offline notification:", err);
            }
        };

        const notifyDeviceOnline = async (device) => {
            const urlon = `${WHATSAPP_ALERT_BASE_URL}Online: `;
            const finalurlon = urlon + device.device_id + " Lat: " + device.lat + " Long: " + device.lon;
            try {
                fetch(finalurlon, { mode: 'no-cors' });
            } catch (err) {
                console.error("Failed to send online notification:", err);
            }
        };

        const fetchDevices = async (showSpinner = false) => {
            if (!isAuthenticated || !loggedInUser) return [];
            if (showSpinner) setLoading(true);
            setError(null);
            try {
                const response = await fetchWithTimeout(`${API_BASE_URL}/api/devices`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user: loggedInUser }),
                    cache: 'no-cache'
                }, 30000);
                if (!response.ok) {
                    let details = '';
                    try {
                        const errorData = await response.json();
                        details = errorData?.error || errorData?.message || response.statusText || '';
                    } catch (_) {
                        details = response.statusText || '';
                    }
                    throw new Error(details ? `Error: ${details}` : `Error: HTTP ${response.status}`);
                }
                const data = await response.json();
                setDevices(data);
                prevDevicesRef.current = data;
                return data;
            } catch (err) {
                if (err?.name === 'AbortError') {
                    setError('Request timed out. Server is slow or unreachable.');
                } else {
                    setError(err.message);
                }
                return null;
            } finally {
                if (showSpinner) setLoading(false);
            }
        };

        // --- Main useEffect for fetching data ---
        useEffect(() => {
            if (!isAuthenticated || !loggedInUser) {
                setDevices([]);
                return;
            }

            const POLL_MS = 5000;
            let timerId = null;
            let cancelled = false;

            const fetchBackgroundData = async () => {
                if (document.visibilityState !== 'visible') {
                    // Do not fetch, but DO schedule the next poll to keep loop alive
                    if (!cancelled) {
                        timerId = setTimeout(runPollLoop, POLL_MS);
                    }
                    return;
                }
                try {
                    const prevDevices = prevDevicesRef.current;
                    const newDevices = await fetchDevices(false);
                    if (!Array.isArray(newDevices)) {
                        if (!cancelled) timerId = setTimeout(runPollLoop, POLL_MS);
                        return;
                    }
                    
                    if (prevDevices) {
                        newDevices.forEach(newDevice => {
                            const oldDevice = prevDevices.find(d => d.device_id === newDevice.device_id);
                            if (oldDevice && oldDevice.status === 'online' && newDevice.status === 'offline') {
                                notifyDeviceOffline(newDevice);
                            }
                            if (oldDevice && oldDevice.status === 'offline' && newDevice.status === 'online') {
                                notifyDeviceOnline(newDevice);
                            }
                        });
                    }

                    const confirmedResponse = await fetchWithTimeout(`${API_BASE_URL}/api/events/confirmed`, { cache: 'no-cache' });
                    const confirmedData = await confirmedResponse.json();
                    if (confirmedResponse.ok) {
                    setConfirmedEvents(confirmedData);
                    const synthesizedEewEvents = Array.isArray(confirmedData)
                            ? confirmedData.map((event) => ({
                                devices: event.devices || [],
                                confidence:
                                    event.severity === 'alarm' ? 0.9 :
                                    event.severity === 'watch' ? 0.6 :
                                    0.3,
                                severity: event.severity,
                                confirmed_at: event.confirmed_at,
                            }))
                            : [];
                    setEewEvents(synthesizedEewEvents);
                    setCountdown(null);
                    }

                    try {
                        const debugRes = await fetchWithTimeout(`${API_BASE_URL}/api/events/candidates/debug`, { cache: 'no-cache' });
                        const debugData = await debugRes.json();
                        if (debugRes.ok) {
                            setCandidateGateDebug(Array.isArray(debugData) ? debugData : []);
                        }
                    } catch (err) {
                        console.error("Candidate debug fetch error:", err);
                    }
                } catch (err) {
                    console.error("Background fetch error:", err.message);
                }
                if (!cancelled) {
                    timerId = setTimeout(runPollLoop, POLL_MS);
                }
            };

            const runPollLoop = async () => {
                await fetchBackgroundData();
            };

            fetchDevices(true);
            timerId = setTimeout(runPollLoop, POLL_MS);
            
            return () => {
                cancelled = true;
                if (timerId) {
                    clearTimeout(timerId);
                }
            };
        }, [isAuthenticated, loggedInUser]);


        // --- Login and Logout Handlers ---
        const handleLogin = async (e) => {
            e.preventDefault();
            setLoginError('');
            try {
                const response = await fetchWithTimeout(`${API_BASE_URL}/api/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username, password }),
                });
                const data = await response.json();
                if (response.ok && data.status === 'success') {
                    setIsAuthenticated(true);
                    setLoggedInUser(data.user);
                    setAuthToken(data.token || null);
                    localStorage.setItem('waivsUser', data.user);
                    localStorage.setItem('waivsAuthToken', data.token || '');
                } else {
                    setLoginError(data.message || 'Invalid username or password.');
                    setPassword('');
                }
            } catch (err) {
                if (err?.name === 'AbortError') {
                    setLoginError('Login timed out. Server/network is slow.');
                } else {
                    setLoginError('Failed to connect to the server.');
                }
            }
        };

        const handleLogout = () => {
            setIsAuthenticated(false);
            setLoggedInUser(null);
            setAuthToken(null);
            setUsername('');
            setPassword('');
            localStorage.removeItem('waivsUser');
            localStorage.removeItem('waivsAuthToken');
        };
        
        // --- Handler for the 'Files' button ---
        const handleOpenFiles = async (device) => {
            if (!device?.soh?.ip_address) {
                alert("Device IP address is not available. Cannot open files page.");
                return;
            }
            await handleCommand(device.device_id, { command: "StartWebserver" });
            alert("Sent command to start the device's file server. The page will open in 3 seconds...");
            setTimeout(() => {
                window.open(`http://${device.soh.ip_address}`, '_blank', 'noopener,noreferrer');
            }, 3000);
        };

        // --- Other handlers ---
        const handleWifiReset = (deviceId) => {
            if (window.confirm("Are you sure you want to erase all saved WiFi networks on this device?")) {
                handleCommand(deviceId, { command: "WifiReset" });
            }
        };
        const openTopicEditor = (device) => { setSelectedDevice(device); setTopicModalOpen(true); };
        const openOtaUpdater = (device) => { setSelectedDevice(device); setOtaModalOpen(true); };
        const openWifiManager = (device) => { setSelectedDevice(device); setWifiModalOpen(true); };
        const openThresholdEditor = async (device) => {
            setSelectedDevice(device);
            try {
                setCurrentThresholds(await loadThresholdPreset(device?.assigned_profile || 'default'));
            } catch (error) {
                console.error("Failed to load threshold preset:", error);
                alert(`Could not load the assigned EEW profile preset: ${error.message}`);
                return;
            }
            setThresholdModalOpen(true);
        };
        const openRuntimeFlagsEditor = (device) => { setSelectedDevice(device); setRuntimeFlagsModalOpen(true); };
        const openBroadcastThresholdEditor = async () => {
            setSelectedDevice(null);
            try {
                setCurrentThresholds(await loadThresholdPreset('default'));
            } catch (error) {
                console.error("Failed to load default threshold preset:", error);
                alert(`Could not load the default EEW profile preset: ${error.message}`);
                return;
            }
            setThresholdModalOpen(true);
        };
        const openEewConfigEditor = () => { setEewConfigModalOpen(true); };
        const openAssignmentEditor = (device) => { setSelectedDevice(device); setAssignmentModalOpen(true); };
        const openRegistryEditor = () => { setRegistryModalOpen(true); };

        const handleAssignmentSaved = (updated) => {
            setDevices(prev => prev.map(d => d.device_id === updated.device_id ? {
                ...d,
                display_name: updated.display_name || d.display_name,
                cluster_id: updated.cluster_id,
                assigned_profile: updated.assigned_profile,
                assigned_profile_version: updated.assigned_profile_version ?? d.assigned_profile_version,
            } : d));
        };

        const handleRuntimeFlagsSaved = (result) => {
            if (result?.device) {
                setDevices(prev => prev.map(d => d.device_id === result.device.device_id ? {
                    ...d,
                    ...result.device,
                    soh: {
                        ...(d.soh || {}),
                        ...(result.device.soh || {}),
                    },
                } : d));
                setSelectedDevice(prev => prev?.device_id === result.device.device_id ? {
                    ...prev,
                    ...result.device,
                    soh: {
                        ...(prev.soh || {}),
                        ...(result.device.soh || {}),
                    },
                } : prev);
            } else {
                fetchDevices(false);
            }
        };
        

        // --- Login Form Rendering ---
        if (!isAuthenticated) {
            return (
                <div style={styles.loginContainer}>
                    <style>{`
                        @keyframes adminDashboardSpin {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                    <div style={styles.loginBox}>
                        <h2 style={styles.loginHeader}>Admin Dashboard Login</h2>
                        <form onSubmit={handleLogin}>
                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>Username</label>
                                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} style={styles.textInput}/>
                            </div>
                            <div style={styles.inputGroup}>
                                <label style={styles.inputLabel}>Password</label>
                                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={styles.textInput}/>
                            </div>
                            {loginError && <p style={styles.loginError}>{loginError}</p>}
                            <button type="submit" style={{ ...styles.loginButton }}>Login</button>
                        </form>
                    </div>
                </div>
            );
        }

        if (loading) return <div style={styles.dashboardContainer}>Loading...</div>;
        if (error) return <div style={{...styles.dashboardContainer, color: 'red'}}>Error: {error}</div>;

        const mostRecentEvent = confirmedEvents.length > 0 ? confirmedEvents[0] : null;
        const recentCandidateDebug = candidateGateDebug.slice(0, 6);

        // --- Main Dashboard Rendering ---
        return (
            <div style={styles.dashboardContainer}>
                <style>{`
                    @keyframes adminDashboardSpin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
                {mostRecentEvent && (
                    <div style={{ padding: '20px', backgroundColor: '#c53030', color: 'white', borderRadius: '8px', marginBottom: '20px', textAlign: 'center' }}>
                        <h2 style={{ margin: 0 }}>CONFIRMED SEISMIC EVENT DETECTED!</h2>
                        <p style={{ margin: '8px 0 0 0' }}><strong>Time:</strong> {new Date(mostRecentEvent.confirmed_at * 1000).toLocaleString()}</p>
                        <p style={{ margin: '8px 0 0 0' }}><strong>Triggered by devices:</strong> {mostRecentEvent.devices.join(', ')}</p>
                    </div>
                )}
                
                {/* ================= EEW COMMAND CENTER ================= */}
                <div style={{
                    background: '#1a202c',
                    color: 'white',
                    padding: '16px',
                    borderRadius: '10px',
                    marginBottom: '20px'
                }}>
                    <h2 style={{margin: 0}}>⚡ EEW Command Center</h2>

                    {eewEvents.length > 0 ? (
                        <>
                            <div style={{marginTop: '10px'}}>
                                <strong>Devices:</strong> {eewEvents[0].devices.length}
                            </div>

                            <div>
                                <strong>Confidence:</strong> {(eewEvents[0].confidence || 0).toFixed(2)}
                            </div>

                            <div style={{
                                marginTop: '10px',
                                padding: '12px',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                background:
                                    eewEvents[0].confidence > 0.7 ? '#c53030' :
                                    eewEvents[0].confidence > 0.5 ? '#dd6b20' :
                                    '#4a5568'
                            }}>
                                {eewEvents[0].confidence > 0.7
                                    ? '🚨 EARTHQUAKE ALERT'
                                    : eewEvents[0].confidence > 0.5
                                    ? '⚠️ WATCH MODE'
                                    : '🟢 LOW SIGNAL'}
                            </div>
                        </>
                    ) : (
                        <p style={{marginTop: '10px'}}>No active seismic events</p>
                    )}

                    {countdown !== null && (
                        <div style={{
                            marginTop: '10px',
                            padding: '10px',
                            background: '#2b6cb0',
                            borderRadius: '6px',
                            textAlign: 'center'
                        }}>
                            ⏱ Estimated arrival: {countdown.toFixed(1)} sec
                        </div>
                    )}
                </div>

                <div style={{
                    background: '#f7fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    padding: '16px',
                    marginBottom: '20px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '10px', flexWrap: 'wrap' }}>
                        <h2 style={{ margin: 0, color: '#2d3748' }}>Classifier Gate Debug</h2>
                        <span style={{ color: '#4a5568', fontSize: '0.9rem' }}>
                            Profile-specific evaluation using each device&apos;s assigned profile
                        </span>
                    </div>
                    {recentCandidateDebug.length === 0 ? (
                        <p style={{ margin: 0, color: '#718096' }}>No recent candidate events.</p>
                    ) : (
                        <div style={{ display: 'grid', gap: '12px' }}>
                            {recentCandidateDebug.map((item, idx) => {
                                const gates = item.gate_eval || {};
                                const passes = gates.passes || {};
                                const values = gates.values || {};
                                const thresholds = gates.thresholds || {};
                                const failed = gates.failed_gates || [];
                                return (
                                    <div key={`${item.device_id || 'unknown'}-${item.received_at || idx}`} style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        background: 'white'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                            <div>
                                                <strong>{item.device_id || 'unknown'}</strong> in cluster <strong>{item.cluster_id}</strong>
                                                <div style={{ color: '#4a5568', fontSize: '0.9rem', marginTop: '4px' }}>
                                                    Assigned profile: <strong>{item.assigned_profile}</strong> v{item.profile_version} | Event: <strong>{item.event_type}</strong>
                                                </div>
                                            </div>
                                            <div style={{ color: '#4a5568', fontSize: '0.9rem' }}>
                                                EQ score {Number(item.eq_score ?? gates.score ?? 0).toFixed(0)} | Pd {Number(item.pd || 0).toFixed(5)} | STA/LTA {Number(item.stalta_ratio || 0).toFixed(2)}
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', marginTop: '10px' }}>
                                            {[
                                                ['Onset', 'onset_slope'],
                                                ['Entropy', 'entropy'],
                                                ['Z-Ratio', 'zratio'],
                                                ['FreqRatio', 'freqratio'],
                                                ['Growth', 'growth'],
                                            ].map(([label, key]) => (
                                                <div key={key} style={{
                                                    borderRadius: '6px',
                                                    padding: '8px',
                                                    background: passes[key] ? '#f0fff4' : '#fff5f5',
                                                    border: `1px solid ${passes[key] ? '#9ae6b4' : '#feb2b2'}`
                                                }}>
                                                    <div style={{ fontWeight: 600, color: passes[key] ? '#276749' : '#c53030' }}>
                                                        {label}: {passes[key] ? 'PASS' : 'FAIL'}
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: '#4a5568' }}>
                                                        {Number(values[key] || 0).toFixed(4)} / {Number(thresholds[key] || 0).toFixed(4)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ marginTop: '8px', fontSize: '0.9rem', color: failed.length ? '#c53030' : '#276749' }}>
                                            {failed.length ? `Rejecting gates: ${failed.join(', ')}` : 'All classifier gates passed.'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                    <h1 style={styles.header}>Admin Dashboard</h1>
                    <div style={{textAlign: 'right'}}>
                        <span style={{marginRight: '15px'}}>Welcome, <strong>{loggedInUser}</strong></span>
                        <button onClick={handleLogout} style={{...styles.button, backgroundColor: '#718096'}}>Logout</button>
                    </div>
                </div>
                
                <div style={{display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px'}}>
                    <button onClick={() => setShowMap(!showMap)} style={{...styles.button, backgroundColor: '#319795', padding: '10px 15px', fontSize: '1rem'}}>
                        {showMap ? 'Hide Map & Status' : 'Show Map & Status'}
                    </button>
                    <button onClick={() => setShowGraph(!showGraph)} style={{...styles.button, backgroundColor: '#4299e1', padding: '10px 15px', fontSize: '1rem'}}>
                        {showGraph ? 'Hide Live Graph' : 'Show Live Graph'}
                    </button>
                    <button onClick={() => setIsDataBrowserOpen(true)} style={{...styles.button, backgroundColor: '#dd6b20', padding: '10px 15px', fontSize: '1rem'}}>
                        Browse Data Files
                    </button>
                    <button onClick={handleGraph} style={{...styles.button, backgroundColor: '#e53e3e', padding: '10px 15px', fontSize: '1rem'}}>
                        SeismoGraphs Tool
                    </button>
                </div>

                {showGraph && (
                    <>
                        <hr style={{margin: '40px 0'}} />
                        <LiveGraph loggedInUser={loggedInUser} authToken={authToken} />
                    </>
                )}

                {showMap && (
                    <>
                        <hr style={{margin: '20px 0'}} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', marginBottom: '20px' }}>
                            <div>
                                <DeviceMap loggedInUser={loggedInUser} />
                            </div>
                            <div>
                                <h2 style={{ textAlign: 'center', color: '#2d3748' }}>Device Fleet Status</h2>
                                <div style={{ overflowX: 'auto', maxHeight: '400px' }}>
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={styles.th}>Device ID</th>
                                                {loggedInUser === 'admin' && <th style={styles.th}>Owner</th>}
                                                <th style={styles.th}>Status</th>
                                                <th style={styles.th}>Last Seen</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {devices.map((device) => (
                                                <tr key={device.device_id}>
                                                    <td style={styles.td}>{device.display_name || device.device_id}</td>
                                                    {loggedInUser === 'admin' && <td style={styles.td}>{device.owner || 'N/A'}</td>}
                                                    <td style={styles.td}>
                                                        <span style={{ ...styles.statusBadge, ...(device.status === 'online' ? styles.statusOnline : styles.statusOffline) }}>
                                                            {device.status}
                                                        </span>
                                                    </td>
                                                    <td style={styles.td}>{device.lastMessage ? new Date(device.lastMessage).toLocaleString() : 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                
                <hr style={{margin: '20px 0'}} />

                <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '20px'}}>
                    {loggedInUser === 'admin' && (
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                onClick={openEewConfigEditor}
                                style={{...styles.button, backgroundColor: '#2b6cb0', padding: '10px 15px', fontSize: '0.9rem'}}>
                                EEW Server Policy
                            </button>
    
                            <button 
                                onClick={openBroadcastThresholdEditor} 
                                style={{...styles.button, backgroundColor: '#dd6b20', padding: '10px 15px', fontSize: '0.9rem'}}>
                                Set EEW Thresholds (All Devices)
                            </button>
                        </div>
                    )}
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h2 style={{ textAlign: 'center', color: '#2d3748', margin: 0 }}>Device Management</h2>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', color: '#4a5568', fontSize: '0.85rem' }}>
                                <span>Sync: {driftSummary.ok}</span>
                                <span style={{ color: '#c53030' }}>Drift: {driftSummary.drift}</span>
                                <span style={{ color: '#9c4221' }}>Not Reported: {driftSummary.unknown}</span>
                            </div>
                            <select
                                value={deviceDriftFilter}
                                onChange={(e) => setDeviceDriftFilter(e.target.value)}
                                style={{
                                    padding: '8px 10px',
                                    borderRadius: '6px',
                                    border: '1px solid #cbd5e0',
                                    color: '#2d3748',
                                    backgroundColor: '#fff'
                                }}>
                                <option value="all">All Devices</option>
                                <option value="drift">Drift Only</option>
                                <option value="unknown">Not Reported</option>
                                <option value="ok">In Sync Only</option>
                            </select>
                            {deviceDriftFilter !== 'all' && (
                                <button
                                    onClick={() => setDeviceDriftFilter('all')}
                                    style={{...styles.button, backgroundColor: '#718096', padding: '8px 12px', fontSize: '0.85rem'}}>
                                    Clear Filter
                                </button>
                            )}
                            {loggedInUser === 'admin' && (
                                <button
                                    id="manage-devices-button-inline"
                                    onClick={openRegistryEditor}
                                    style={{...styles.button, backgroundColor: '#2f855a', padding: '8px 12px', fontSize: '0.85rem'}}>
                                    Manage Devices
                                </button>
                            )}
                        </div>
                    </div>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Device ID</th>
                                <th style={styles.th}>Name</th>
                                {/* <th style={styles.th}>Applied Tuning</th> */}
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Confidence</th>
                                <th style={{...styles.th, textAlign: 'center'}}>Actions</th>
                                {loggedInUser === 'admin' && <th style={styles.th}>Owner</th>}
                                <th style={styles.th}>IP Address</th>
                                <th style={styles.th}>Firmware</th>
                                <th style={styles.th}>Lat</th>
                                <th style={styles.th}>Long</th>
                                <th style={styles.th}>Cluster</th>
                                <th style={styles.th}>Assigned Profile</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDevices.map((device) => {
                                const profileDrift = getProfileDriftState(device);
                                const displayConfidence = getDisplayConfidence(device);
                                const appliedToneStyle =
                                    profileDrift.tone === 'drift'
                                        ? { backgroundColor: '#fff5f5', borderColor: '#feb2b2', color: '#c53030' }
                                        : profileDrift.tone === 'ok'
                                            ? { backgroundColor: '#f0fff4', borderColor: '#9ae6b4', color: '#276749' }
                                            : { backgroundColor: '#fffaf0', borderColor: '#f6ad55', color: '#9c4221' };

                                return (
                                <tr key={device.device_id}>
                                    <td style={styles.td}>{device.device_id}</td>
                                    <td style={styles.td}>{device.display_name || device.device_id}</td>
                                    {/* <td style={styles.td}>
                                        <div style={{
                                            display: 'inline-flex',
                                            flexDirection: 'column',
                                            gap: '4px',
                                            padding: '6px 8px',
                                            borderRadius: '8px',
                                            border: '1px solid',
                                            ...appliedToneStyle
                                        }}>
                                            <span style={{ fontWeight: 700 }}>{profileDrift.label}</span>
                                            <span>{profileDrift.detail}</span>
                                        </div>
                                    </td> */}
                                    <td style={styles.td}>
                                        <span style={{ ...styles.statusBadge, ...(device.status === 'online' ? styles.statusOnline : styles.statusOffline) }}>
                                            {device.status}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        <span style={{
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            background:
                                                displayConfidence === null ? '#718096' :
                                                displayConfidence > 0.7 ? '#38a169' :
                                                displayConfidence > 0.5 ? '#dd6b20' :
                                                '#718096',
                                            color: 'white'
                                        }}>
                                            {displayConfidence === null ? 'N/A' : displayConfidence.toFixed(2)}
                                        </span>
                                    </td>
                                    <td style={{...styles.td, ...styles.actionsCell}}>
                                                                        {loggedInUser === 'admin' && (
                                            <button onClick={() => openRuntimeFlagsEditor(device)} style={{...styles.button, backgroundColor: '#6b46c1'}}>Runtime Flags</button>
                                        )}
                                        <button onClick={() => openOtaUpdater(device)} style={{...styles.button, backgroundColor: '#805ad5'}}>OTA Update</button>
                                        <button onClick={() => openThresholdEditor(device)} style={{...styles.button, backgroundColor: '#dd6b20'}}>Thresholds</button>
                                        <button onClick={() => handleCommand(device.device_id, {command: 'Restart'})} style={{...styles.button, backgroundColor: '#4299e1'}}>Restart</button>
                                        {loggedInUser === 'admin' && (
                                            <button onClick={() => openLogViewer(device)} style={{...styles.button, backgroundColor: '#4a5568'}}>View Logs</button>
                                        )}
                                        <button onClick={() => handleOpenFiles(device)} disabled={!device?.soh?.ip_address} style={{...styles.button, backgroundColor: '#718096', ...( !device?.soh?.ip_address ? {backgroundColor: '#e2e8f0', cursor: 'not-allowed'} : {})}}>SD Card (Same Network)</button>
                                        <button onClick={() => handleCommand(device.device_id, {command: 'shutdown'})} style={{...styles.button, backgroundColor: '#FF0000'}}>Shutdown_Facility</button>
                                        <button onClick={() => handleCommand(device.device_id, {command: 'reset_relay'})} style={{...styles.button, backgroundColor: '#006400'}}>Reset_Relay</button>
                                        <button onClick={() => openTopicEditor(device)} style={{...styles.button, backgroundColor: '#dd6b20'}}>Topics</button>
                                        {loggedInUser === 'admin' && (
                                            <button onClick={() => openAssignmentEditor(device)} style={{...styles.button, backgroundColor: '#2b6cb0'}}>Assignment</button>
                                        )}
                                        <button onClick={() => handleWifiReset(device.device_id)} style={{...styles.button, backgroundColor: '#c53030'}}>Reset WiFi</button>
                                        <button onClick={() => openWifiManager(device)} style={{...styles.button, backgroundColor: '#319795'}}>Add WiFi</button>

                                    </td>
                                    {loggedInUser === 'admin' && <td style={styles.td}>{device.owner || 'N/A'}</td>}
                                    <td style={styles.td}>{device.soh?.ip_address || 'N/A'}</td>
                                    <td style={styles.td}>{device.soh?.fw_version || 'N/A'}</td>
                                    <td style={styles.td}>{formatCoordinate(device.lat)}</td>
                                    <td style={styles.td}>{formatCoordinate(device.lon)}</td>
                                    <td style={styles.td}>{device.cluster_id || 'default_cluster'}</td>
                                    <td style={styles.td}>
                                        {(device.assigned_profile || 'default')} v{device.assigned_profile_version ?? 1}
                                    </td>
                                </tr>
                            )})}
                            {filteredDevices.length === 0 && (
                                <tr>
                                    <td
                                        style={{...styles.td, textAlign: 'center', color: '#718096', padding: '16px'}}
                                        colSpan={loggedInUser === 'admin' ? 12 : 11}>
                                        No devices match the current tuning sync filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                
                {/* Modal rendering */}
                {isTopicModalOpen && <TopicModal device={selectedDevice} onClose={() => setTopicModalOpen(false)} onSave={handleCommand} />}
                {isOtaModalOpen && <OtaModal device={selectedDevice} onClose={() => setOtaModalOpen(false)} onSave={handleCommand} />}
                {isWifiModalOpen && <WifiModal device={selectedDevice} onClose={() => setWifiModalOpen(false)} onSave={handleCommand} />}
                {isThresholdModalOpen && <ThresholdModal 
                    device={selectedDevice}
                    currentThresholds={currentThresholds}
                    onClose={() => setThresholdModalOpen(false)} 
                    onSave={handleThresholdSave} />}
                {isRuntimeFlagsModalOpen && selectedDevice && (
                    <RuntimeFlagsModal
                        device={selectedDevice}
                        loggedInUser={loggedInUser}
                        authToken={authToken}
                        onClose={() => setRuntimeFlagsModalOpen(false)}
                        onSaved={handleRuntimeFlagsSaved}
                    />
                )}
                {isEewConfigModalOpen && <EewConfigModal loggedInUser={loggedInUser} authToken={authToken} onClose={() => setEewConfigModalOpen(false)} />}
                {isAssignmentModalOpen && selectedDevice && <DeviceAssignmentModal device={selectedDevice} loggedInUser={loggedInUser} authToken={authToken} onClose={() => setAssignmentModalOpen(false)} onSaved={handleAssignmentSaved} />}
                {isRegistryModalOpen && <DeviceRegistryModal loggedInUser={loggedInUser} authToken={authToken} onClose={() => setRegistryModalOpen(false)} onSaved={() => fetchDevices(false)} />}
                {isDataBrowserOpen && <DataBrowser loggedInUser={loggedInUser} authToken={authToken} onClose={() => setIsDataBrowserOpen(false)} />}
                {isSdCardBrowserOpen && selectedDevice && <SdCardBrowser device={selectedDevice} loggedInUser={loggedInUser} authToken={authToken} onClose={() => setIsSdCardBrowserOpen(false)} />}
                {isLogViewerOpen && selectedDevice && <LogViewerModal device={selectedDevice} loggedInUser={loggedInUser} authToken={authToken} onClose={() => setIsLogViewerOpen(false)} />}

                    
            </div>
        );
    };

    export default AdminDashboard;  
